const formatT = (str, vars) => {
  if (!str || typeof str !== 'string') return '';
  if (!vars || typeof vars !== 'object') return str;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? '')),
    str,
  );
};

export { formatT };

export const translations = {
  en: {
    network: {
      offlineTitle: 'Offline',
      offlineBody: 'Live updates are paused. Reconnect to refresh your ride.',
    },
    langToggle: 'తెలుగు',
    common: {
      yes: 'Yes',
      no: 'No',
      add: 'Add',
      seeAll: 'See all',
      cancel: 'Cancel',
      save: 'Save',
      back: 'Back',
      close: 'Close',
      done: 'Done',
      ok: 'OK',
      loading: 'Loading',
    },
    del: {
      title: 'Delete Account',
      subtitle: 'Permanently remove your account and personal data',
      phoneInvalid: 'Please enter a valid 10-digit phone number',
      submitted: 'Account deletion request submitted',
      submitFail: 'Could not submit request. Please contact support.',
      valuedUser: 'Valued User',
      received: 'Request Received',
      receivedBody: 'Your data deletion request for +91 {phone} has been registered. Our privacy officer will process the erasure within 30 days.',
      trackWhatsapp: 'Track via WhatsApp Support',
      irreversibleLabel: 'Irreversible Action:',
      irreversibleBody: 'Deleting your account permanently erases your profile, trip history, emergency contacts, and active tokens. Account access will cease immediately.',
      phoneLabel: 'Registered phone number',
      phonePh: '10-digit mobile number',
      reasonLabel: 'Reason for leaving',
      feedbackLabel: 'Additional feedback (optional)',
      feedbackPh: 'Tell us how we could improve…',
      submitting: 'Submitting request…',
      confirm: 'Confirm & request deletion',
      keep: 'Keep my account',
      reasonNotUsing: 'Not using MyTownly anymore',
      reasonPrivacy: 'Privacy & data concerns',
      reasonSwitched: 'Switched to another service',
      reasonNewAccount: 'Creating a new account',
      reasonOther: 'Other reason',
    },
    privacy: {
      title: 'Privacy & Data Center',
      subtitle: 'Manage your personal data, preferences, and privacy',
      legalHead: 'Legal & Policies',
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Use',
      prefsHead: 'Privacy Preferences',
      analyticsTitle: 'Analytics Opt-Out',
      analyticsSub: 'Disable usage diagnostics & event telemetry. Core trip processing is unaffected.',
      marketingTitle: 'Promotional Updates',
      marketingSub: 'Receive occasional ride discounts & town announcements.',
      saving: 'Saving…',
      savePrefs: 'Save Preferences',
      nomineeHead: 'Trusted Nominee',
      nomineeSub: 'Add a trusted contact who can manage your account and data if you are unable to.',
      nomineeNamePh: 'Nominee full name',
      nomineePhonePh: 'Nominee 10-digit phone',
      saveNominee: 'Save Nominee',
      dataHead: 'Your Data & Account',
      downloadTitle: 'Download Personal Data',
      downloadSub: 'Export a copy of your processed records',
      deleteTitle: 'Delete Account & Erase Data',
      deleteSub: 'Permanent removal of your profile',
      grievanceHead: 'File a Privacy Grievance',
      grievanceSub: 'Have an issue regarding how your data is handled? Submit directly to our compliance desk.',
      grievancePh: 'Describe your concern in detail…',
      submitting: 'Submitting…',
      submitGrievance: 'Submit Grievance',
      grievanceOfficer: 'Grievance Officer:',
      whatsappDesk: 'WhatsApp Desk ›',
      prefsUpdated: 'Preferences updated',
      prefsSaveFail: 'Could not save preferences',
      nomineePhoneInvalid: 'Enter a valid 10-digit nominee phone',
      nomineeSaved: 'Nominee saved',
      nomineeCleared: 'Nominee cleared',
      nomineeSaveFail: 'Could not save nominee',
      accessSubmitted: 'Access request submitted',
      requestFail: 'Could not submit request',
      grievanceMin: 'Please describe your concern (min 10 characters)',
      grievanceSubmitted: 'Grievance submitted',
      grievanceFail: 'Could not submit grievance',
    },
    support: {
      title: 'How can we help you?',
      forRide: 'Support for Ride #{id}',
      destination: 'Destination',
      selectCategory: 'Select Issue Category',
      describe: 'Describe issue or question (optional)',
      notesPh: 'Type your notes or question here…',
      continueWhatsapp: 'Continue to WhatsApp Support',
      attachmentHint: 'If you have a screenshot or photo, attach it after WhatsApp opens.',
      caseCreated: 'Support case opened',
      opening: 'Opening…',
      replyHint: 'Our team typically replies in under 1 hour.',
      catFare: 'Fare & Payment',
      catLost: 'Lost Item',
      catCaptain: 'Driver Feedback',
      catOther: 'General Query',
    },
    legal: {
      lastUpdated: 'Last updated',
    },
    splash: { loading: 'Loading...' },
    login: {
      tcFailed: 'Truecaller login failed:',
      tcFailedUseOtp: 'Truecaller login failed. Please use OTP.',
      tcNotUsable: 'Open Truecaller app, complete your profile, then try again.',
      tcCantStart: 'Truecaller could not start. Use OTP below.',
      tcPluginMissing: 'Truecaller plugin missing — reinstall the latest APK.',
      tcUnavailable: 'Truecaller unavailable. Use OTP below.',
      tcCantOpen: 'Could not open Truecaller. Use OTP below.',
      signingInTruecaller: 'Signing in via Truecaller…',
      verifyingProfile: 'Verifying your profile securely',
      tcTimeoutUseOtp: 'Truecaller took too long. Please verify via OTP.',
      stillSigningIn: 'Still signing you in — this can take a moment…',
      verifyingOtp: 'Verifying OTP…',
      loggingIn: 'Logging into your account',
      otpAriaLabel: 'Login OTP',
      title: 'MyTownly Rides',
      subtitle: 'Book autos in {city}',
      headline: 'Autos in {city} in',
      headlineAccent: 'minutes',
      phoneLabel: 'Mobile number',
      phonePlaceholder: '10-digit number',
      termsPrefix: 'By continuing, you confirm you are 18+ and agree to our',
      termsLink: 'Terms of Use',
      termsAnd: '&',
      privacyLink: 'Privacy Policy',
      continue: 'Continue',
      skip: 'Skip',
      sendOtp: 'Send OTP',
      sendingOtp: 'Sending OTP…',
      verifyOtp: 'Verify & continue',
      otpSent: 'OTP sent!',
      resend: 'Resend OTP',
      resendWait: 'Resend in {s}s',
      otpValidHint: 'Code valid for 5 minutes. Do not resend unless needed.',
      nameLabel: 'Your name',
      namePlaceholder: 'How should Anna call you?',
      nameInvalid: 'Enter your name in letters (not a phone number)',
      saveName: 'Continue',
      changeNumber: 'Change number',
      invalidPhone: 'Enter a valid 10-digit number',
      invalidOtp: 'Enter the 6-digit OTP',
      sendingOtpToast: 'Sending OTP… complete any verification screen if it opens.',
      tcAppOnly: 'Truecaller works in the MyTownly Rides app',
      resendOtpHint: 'Tap Resend OTP for a fresh code',
    },
    authErrors: {
      invalidPhone: 'Invalid phone number',
      tooManyRequests: 'Too many attempts. Wait a few minutes.',
      captchaFailed: 'Security check failed. Refresh and try again.',
      invalidOtp: 'Wrong OTP',
      codeExpired: 'OTP expired. Resend.',
      default: 'Something went wrong. Try again.',
      noOtpSession: 'Session expired. Request a new OTP.',
      refererBlocked: 'Login isn’t available right now. Please try again in a little while.',
      appNotAuthorized: 'Phone login isn’t working on this app right now. Please update the app or try again later.',
      nativeOtpFailed: 'Phone login failed. Please try again, or update the app.',
      timeout: 'SMS did not arrive in time. Check signal and try again.',
      phoneNotEnabled: 'Phone login isn’t available right now. Please try again later.',
      regionNotEnabled: 'We couldn’t send an OTP to this number. Please check it or try again later.',
      missingOtp: 'Enter the OTP',
      quotaExceeded: 'SMS limit reached. Try again later.',
      networkFailed: 'Network problem. Check your connection and try again.',
    },
    book: {
      greetMorning: 'Good morning,',
      greetAfternoon: 'Good afternoon,',
      greetEvening: 'Good evening,',
      riderFallback: 'User',
      riderThere: 'there',
      menu: 'Menu',
      mapView: 'Map',
      satelliteView: 'Satellite',
      toggleSatellite: 'Toggle satellite map',
      showDetails: 'Show details',
      showMap: 'Collapse to map',
      whereTo: 'Where to?',
      whereHeaded: 'Where are you headed?',
      youAreHere: 'You are here',
      quickDestinations: 'Quick Destinations',
      recentPlaces: 'Recent places',
      seeAll: 'See all',
      add: 'Add',
      currentLocation: 'Your current location',
      setPickup: 'Set pickup location',
      pickupOutside: 'Outside service area',
      setPickupFirst: 'Set your pickup first',
      recent: 'Recent places',
      trustCash: 'Cash on drop',
      trustOtp: 'OTP verified start',
      serviceRadius: 'service area',
      fareBase: 'Base fare',
      fareDistance: 'Distance ({km} km × ₹{rate})',
      payOnDrop: 'Pay Anna at drop',
      auto: 'Auto',
      bookAutoFor: 'Book Auto · ₹{fare}',
      pickup: 'Pickup',
      drop: 'Drop',
      pickupPlaceholder: 'Search pickup in {city}',
      useCurrentLocation: 'Use my location',
      chooseTownTitle: 'Choose your town',
      chooseTownBody: 'Select the town where you want to book your auto.',
      townLoadTitle: 'Unable to load towns',
      townLoadBody: 'Check your connection and try again. Your saved town is still remembered.',
      townLoadBodyNoSaved: 'Check your connection and try again.',
      townLoadRetry: 'Try again',
      townLoadRetrying: 'Trying…',
      switchTownTitle: 'Switch town',
      switchTownBody: 'Your pickup must be inside the selected town’s service area.',
      orChooseTown: 'or choose a town',
      townUnavailable: 'This town is not available yet.',
      locationOutsideTown: 'Your location is outside our active service towns. Choose a town to continue.',
      locating: 'Getting location...',
      gettingAddress: 'Getting address…',
      change: 'Change',
      serviceArea: 'Rides within {km} km of {city}',
      outsideArea: 'Only locations within {km} km of {city} are supported',
      intertownRedirectHint: 'Please book this trip using Intertown.',
      intertownRedirectCta: 'Book with Intertown',
      geoDenied: 'Location is turned off for MyTownly Rides. Turn it on in Settings to use your current location.',
      openSettings: 'Open Settings',
      locationServicesOff: 'Device location is turned off. Turn on location to find your position.',
      turnOnLocation: 'Turn On Location',
      geoTimeout: 'Location timed out. Try again outdoors.',
      geoUnavailable: 'Could not detect location. Enter pickup manually.',
      locationApprox: 'Couldn’t get GPS — confirm pickup on the map before booking',
      locationApproxHint: 'GPS is approximate — fine-tune on the map if needed',
      locationOutsideHint: 'You seem outside {city}. Set pickup in {city} — or book for someone there.',
      zoneComingSoon: 'MyTownly is coming soon to {city}! We’ll let you know the moment autos are available there.',
      forOtherTitle: 'Booking for someone else?',
      forOtherFarPickup: 'Pickup is {km} km away from your location. Are you booking for someone else?',
      forOtherOutOfTown: 'You appear to be outside {city}. Are you booking for someone else in town?',
      forMyself: 'For myself',
      forSomeoneElse: 'For someone else',
      setPickupInTown: 'Set a pickup inside {city} to continue',
      riderDetailsTitle: 'Rider details',
      riderDetailsHint: 'Anna will call this person at pickup.',
      riderName: 'Rider name',
      riderNamePlaceholder: 'Name of person riding',
      riderPhone: 'Rider phone',
      riderPhonePlaceholder: '10-digit number',
      riderNameRequired: 'Enter rider name in letters (not a phone number)',
      riderPhoneRequired: 'Enter valid 10-digit rider phone',
      riderContinue: 'Continue',
      back: 'Back',
      ridingFor: 'Rider: {name}',
      riderSaved: 'Riding for {name}',
      bookingPaused: 'Bookings are temporarily paused. Please try again later.',
      pausedTitle: 'Bookings paused',
      pausedBody: 'New bookings are paused right now. Please check back soon.',
      stoppedTitle: 'Bookings unavailable',
      stoppedBody: 'MyTownly is temporarily unavailable. Please try again shortly.',
      fareMinimum: 'Minimum fare',
      estimatedFare: 'Estimated fare',
      distance: '{km} km · ~{min} min',
      bookRide: 'Book auto',
      searching: 'Finding an auto...',
      cash: 'Pay cash to Anna',
      billingError: 'Maps isn’t available right now. Please try again in a little while.',
      mapsError: 'Maps couldn’t load. Check your connection and try again.',
      routeError: 'Could not calculate route',
      placeError: 'Could not load that place. Try again or use “Locate on map”.',
      locationError: 'Could not get your location',
      bookError: 'Could not book ride',
      errZoneInactive: 'MyTownly isn’t available in this area yet.',
      errTooClose: 'Pickup and drop are too close. Choose a farther drop.',
      errTooLong: 'This trip is too long for a local auto. Try Intertown for longer trips.',
      errTooMany: 'Too many tries. Please wait a minute and book again.',
      errBlocked: 'Your account can’t book rides right now. Please contact support.',
      errTipChanged: 'Nearby autos just changed, so the tip options changed. Please review and book again.',
      errWaitingTermsChanged: 'The waiting charge terms just changed. Please check the new terms and book again.',
      waitingTerms: 'Waiting charge: {free} min free, then ₹{rate}/min, paid to your Anna',
      pickupEta: '{min} min away',
      dropAt: 'Drop {time}',
      seatsLabel: 'Seats {n}',
      pickupMid: 'Nearest auto is {km} km away (~{min} min)',
      pickupFar: 'Autos are far ({km} km, ~{min} min)',
      pickupNone: 'No autos nearby right now',
      tipHintMid: 'Add a tip to get an auto faster',
      tipHintFar: 'Recommended: add a tip so an auto reaches you sooner',
      tipHintNone: 'You can still book — we’ll keep searching. A tip helps reach farther.',
      tipHintOffline: 'No autos are online right now. You can still book and we’ll keep looking.',
      pickupTipChosen: 'Pickup tip +₹{amount} added',
      callSupport: 'Call support',
      sharedOutside: '📍 Shared destination is outside the service area',
      sharedDestSet: '📍 Destination set: {name}',
      sharedLocation: 'Shared Location',
      activeRideExists: 'You already have an active ride',
      hi: 'Hi, {name}!',
      readyToGo: 'Where would you like to go?',
      whereGoing: 'Where are you going?',
      pickupFrom: 'Pickup from',
      planTrip: 'Plan your trip',
      confirmRide: 'Confirm your ride',
      editTrip: 'Edit locations',
      swap: 'Swap pickup & drop',
      recenter: 'My location',
      calculating: 'Calculating fare…',
      finishIntertownFirst: 'Finish your Intertown trip first',
      locateOnMap: 'Locate on map',
      pinPickupTitle: 'Set pickup on map',
      pinDropTitle: 'Set drop on map',
      pinHint: 'Move the map — pin stays in the center',
      pinAreaHint: '{name} is a big area. Move the pin to your exact drop point.',
      pinConfirmPickup: 'Confirm pickup',
      pinConfirmDrop: 'Confirm drop',
      pickupCheckTitle: 'Is this your pickup spot?',
      pickupCheckHint: 'Your Anna comes to this pin. Move the map to put it at your gate.',
      pickupCheckMovedHint: 'You moved the pin, so the fare may change. You\'ll see it before you book.',
      pickupCheckBook: 'Confirm & book · ₹{fare}',
      pickupMovedFare: 'You moved the pickup, so the fare changed from ₹{from} to ₹{to}.',
      pickupMovedSameFare: 'Pickup updated. The fare is the same.',
      pinLoading: 'Getting address…',
      pinApprox: 'Selected location',
      adjustOnMap: 'Adjust on map',
      shortcutHome: 'Home',
      shortcutWork: 'Work',
      shortcutBusStand: 'Bus Stand',
      shortcutCrossRoad: 'Cross Road',
      shortcutMarketYard: 'Market Yard',
      shortcutRailway: 'Railway Station',
      shortcutHospital: 'Govt. Hospital',
      saveHomeTitle: 'Save Home Address',
      saveWorkTitle: 'Save Work Address',
      savedHome: 'Saved as Home',
      savedWork: 'Saved as Work',
      yourLocation: 'Your location',
      pinpointingPickup: 'Your location',
      tapToSetPickup: 'Tap to set pickup location',
      selectedPoi: 'Selected',
    },
    active: {
      tripPin: 'Trip PIN',
      tripPinHint: 'Tell this PIN to your Anna',
      viewAnnaProfile: 'View Anna profile',
      tripDetails: 'Trip details',
      rideProgress: 'Ride progress',
      recenterMap: 'Recenter map',
      searchingNearby: 'Looking for autos nearby…',
      nightShieldTitle: 'Travelling late?',
      nightShieldBody: "It's late. Share your live trip with someone you trust in {city}.",
      nightShieldShare: 'Share my live trip',
      nightShieldOk: "I'm okay",
      etaArriving: 'Arriving in {min} min',
      etaArrived: 'Your Anna has arrived',
      etaHeadingMin: '{min} min to your destination',
      etaHeading: 'Heading to your destination',
      etaSubAssigned: 'Anna is on the way to pick you up',
      etaSubArrived: 'Meet your Anna at the pickup point',
      etaSubStarted: 'Sit back and enjoy your ride',
      captainConnecting: 'Your Anna is on the way',
      captainConnectingSub: 'Reconnecting for full trip details…',
      cashPaid: 'You paid ₹{fare} in cash',
      rateYourRideWith: 'Rate your ride with',
      rateStars: 'Rate {n} stars',
      ratingSubmitted: 'Rated {stars} ★ — thank you!',
      sentiment1: 'Poor',
      sentiment2: 'Not great',
      sentiment3: 'Okay',
      sentiment4: 'Good',
      sentiment5: 'Excellent!',
      quickChecks: 'Quick checks',
      qFareCorrect: 'Was the fare correct?',
      qRouteGood: 'Did the Anna take a good route?',
      qExtraMoney: 'Were you asked for extra money?',
      submitRatingFinish: 'Submit & finish',
      bookAnother: 'Book another ride',
      viewReceipt: 'View receipt',
      safetyFirst: 'Safety first',
      safetyShareSub: 'Share your live trip status with family or friends',
      shareLiveTrip: 'Share live trip',
      cancelChargesMayApply: 'Cancellation charges may apply',
      boostFailed: 'Could not boost fare. Try again.',
      title: 'Your ride',
      findingCaptain: 'Finding nearby autos',
      nearbyShort: '{count} nearby',
      nearbyShortOne: '1 nearby',
      lookingNearbyShort: 'Looking…',
      captainAssigned: 'Auto found! On the way to you',
      captainOnWay: 'Auto is on the way',
      annaCancelledSearching: '{name} had to cancel. Finding another Anna for you...',
      searching: 'Looking for an auto nearby',
      assigned: 'Auto is on the way',
      arrived: 'Auto has arrived',
      started: 'Trip in progress',
      tripStarted: 'Trip started! Heading to destination',
      tripStartedToast: 'Trip started! Heading to destination',
      enjoyRide: 'Enjoy your ride!',
      completed: 'Ride completed',
      cancel: 'Cancel ride',
      cancelConfirm: 'Cancel this ride?',
      shareOtp: 'Share OTP with Anna to start',
      shareOtpWithCaptain: 'SHARE OTP WITH ANNA',
      otp: 'Share this OTP with Anna to start trip',
      driver: 'Anna',
      vehicle: 'Vehicle',
      payCash: 'Pay ₹{fare} cash to Anna',
      stepSearch: 'Finding',
      stepAssigned: 'On the way',
      stepArrived: 'Arrived',
      stepTrip: 'On trip',
      stepDone: 'Done',
      eta: 'Trip time ~{time}',
      nearbyCaptains: '{count} autos nearby',
      nearbyCaptainsOne: '1 auto nearby',
      noCaptainsNearby: 'Looking for autos nearby…',
      notifyingNearby: '{count} autos nearby',
      notifyingNearbyOne: '1 auto nearby',
      expandingSearch: 'Expanding search area…',
      ringNear: 'Asking autos closest to you…',
      ringWider: 'Looking a bit farther…',
      ringArea: 'Searching all of {city}…',
      searchTimeout: 'No autos available nearby',
      searchTimeoutHint: 'Keep waiting, book again, or call support',
      noCaptain: 'No autos available right now',
      noCaptainHint: 'Try booking again in a minute — or keep waiting',
      noCaptainHintDistance: 'Nearest Anna is {km} km away right now — try boosting your fare below, or keep waiting',
      doneToRides: 'Back to rides',
      ratePrompt: 'How was your ride?',
      keepWaiting: 'Keep waiting',
      bookAgain: 'Book again',
      callSupport: 'Call support',
      pickupEta: 'Auto arriving in ~{min} min',
      captainAboutToArrive: 'Auto is about to arrive!',
      captainAboutToArriveSub: 'Get ready at your pickup point',
      pickupEtaShort: 'to pickup',
      dropEtaShort: 'to destination',
      etaMin: 'min',
      liveTracking: 'Live tracking',
      tripTime: 'Trip time ~{time}',
      otpReady: 'OTP for your Anna',
      otpValidUntil: 'Tap to copy — share with your Anna to start',
      otpCopied: 'OTP copied',
      distanceTime: 'Distance & time',
      fareBreakdown: 'Fare breakdown',
      fareBreakdownLine: 'Base ₹{base} + Distance ₹{dist} = ₹{total}',
      matchAsk: 'Does auto {no} match?',
      boostTitle: '⚡ Get Auto Faster',
      boostHint: 'Add a tip to get an auto faster',
      boostHintDistance: 'Nearest Anna is {km} km away — boost the fare to reach her faster',
      boostNoCaptains: 'No autos online right now — boosting won’t help yet. We’ll let you know the moment one is available.',
      boostChip: '+₹{amount}',
      boostApplied: 'Fare boosted by +₹{tip} (Total: ₹{fare})',
      boostSuccess: 'Fare boosted by +₹{amount}! Alerting nearby autos.',
      boostTooLate: 'An Anna has already taken your ride, so the fare can’t be boosted now.',
      boostWait: 'Please wait a few seconds before boosting again.',
      boostMax: 'Maximum tip limit reached (₹{cap})',
      matchYes: 'Yes, matches',
      matchNo: 'No, wrong auto',
      matchThanksYes: 'Thanks — recorded',
      matchThanksNo: 'Thanks — we’ll review this Anna',
      matchRecordedYes: 'You confirmed the auto number matches',
      matchRecordedNo: 'Mismatch reported — our team can review',
      call: 'Call',
      callCaptain: 'Call Anna',
      share: 'Share',
      navigate: 'Open navigation',
      navUnavailable: 'Location not available for navigation',
      sos: 'SOS',
      shareTripMsg: 'MyTownly Ride · {status}\nTo: {dest}\nAnna: {name}\nAuto: {vehicle}\nFare: ₹{fare}\nLive track: {link}',
      sharePreparing: 'Preparing live track link…',
      shareReady: 'Link ready — tap Share again',
      shareUnavailable: 'Could not share trip',
      sosUnavailable: 'Support number not set',
      rideNotFound: 'Ride not found',
      rideNotFoundSub: 'This trip may have ended or the link is invalid.',
      showMap: 'View map',
      showDetails: 'Show details',
      fitMap: 'Show all on map',
      tripsCount: '{n} trips',
      safetyCheckTitle: 'Safety Check Before Boarding',
      safetyCheckSub: 'Verify Auto Number {no} & Anna {name} match before sharing your OTP.',
      captainVerified: 'Anna Verified',
      unexpectedStopTitle: 'Is everything okay with your ride?',
      unexpectedStopSub: 'We noticed your vehicle has been stationary for over 5 minutes.',
      locationStale: 'Anna’s location hasn’t updated in a while',
      imSafe: 'I’m Safe',
      keepWaitingNotifying: 'Looking for nearby autos again…',
      searchExpiredToast: 'No auto was available nearby. Please try booking again.',
      waitingTitle: 'Your Anna is waiting',
      waitingFreeLeft: 'Free waiting left {time}',
      waitingSoFar: '₹{amount} waiting so far · {time}',
      waitingTerms: '{free} min free, then ₹{rate}/min',
      waitingHint: 'The charge stops when you share the PIN and the trip starts.',
    },
    details: {
      receiptTitle: 'Ride details',
      rideLoading: 'Loading ride details…',
      rideLoadFailed: 'Could not load this ride. Please try again.',
      retry: 'Try again',
      receiptAutoRide: 'Auto ride',
      receiptFareBreakdown: 'Fare breakdown',
      receiptRebook: 'Book this route again',
      receiptKm: 'km',
      receiptMin: 'min',
      dropoff: 'Drop-off',
      paidInCash: 'Paid in cash',
      paymentReceived: 'Payment received',
      paymentPending: 'Payment pending',
      totalPaid: 'Total paid',
      paid: 'Paid',
      distanceFare: 'Distance fare',
      distanceCalculation: '{km} km × ₹{rate}/km',
      distanceRate: 'Rate: ₹{rate}/km',
      rounded: 'rounded',
      routeMap: 'Pickup and drop-off map',
      mapLoading: 'Loading map…',
      mapUnavailable: 'Map unavailable',
      receiptActions: 'Receipt and support',
      copyId: 'Copy ride ID',
      idCopied: 'Ride ID copied',
      copyFailed: 'Could not copy ride ID',
      yourRating: 'Your rating: {rating} stars',
      estimatedShort: '(est.)',
      minsLabel: 'mins',
      kmsLabel: 'kms',
      autoRide: 'Auto Ride',
      rideId: 'Ride ID',
      tapAway: "We're a tap away",
      title: 'Ride Details',
      addresses: 'Address Details',
      driverCard: 'Driver & Vehicle',
      fareSummary: 'Fare Summary',
      rebookRoute: 'Re-book This Route',
      needHelp: 'Need Help / Call Support',
      unavailable: 'Not available',
      fareBreakdownUnavailable: 'Fare breakdown was not recorded for this older ride.',
      shareReceipt: 'Share receipt',
      reportIssue: 'Report an issue',
      cashCollected: 'Cash recorded as collected',
      cashDue: 'Cash due after ride',
    },
    driver: {
      annaProfile: 'Anna Profile',
      verifiedPartner: 'Verified Partner',
      verifiedPartnerShort: 'Verified',
      communityCaptain: 'Registered Anna',
      newRating: 'New',
      newCaptain: 'New Anna',
      ratingWithCount: 'Rating ({n})',
      rating: 'Rating',
      tripsCount: '{n} trips',
      experience: 'Experience',
      operatingArea: 'Operating area',
      vehicleDetails: 'Vehicle details',
      autoFallback: 'Auto',
      credentials: 'Verified credentials',
      credIdentity: 'Identity verified',
      credIdentitySub: 'Government ID checked',
      credAuto: 'Auto verified',
      credAutoSub: 'Registered in {city}',
      credCash: 'Cash payments',
      credCashSub: 'Pay by cash after the ride',
      verified: 'Verified Anna',
      viewPhoto: 'View photo',
      safetyTitle: 'Auto Safety Report',
      safetySubtitle: 'Your Safety Matters to Us ❤️',
      safetyAudited: 'RTA Audited',
      safetyPassed: '100% Passed',
      safetyRc: 'RC Status',
      safetyInsurance: 'Vehicle Insurance',
      safetyDrivingLicence: 'Driving Licence (DL)',
      safetyValidUpto: 'Valid Upto',
      safetyStatus: 'Status',
      safetyValid: 'Valid',
      safetyPending: 'Pending review',
      safetyExpired: 'Expired',
      safetyUnavailable: 'Not published',
      safetyFooter: 'This auto meets all Min of Road & Transport Safety checks',
      safetyFooterPartial: 'Most safety records are verified; one or more require review',
      safetyFooterUnavailable: 'Safety details have not been published for this auto',
    },
    fare: {
      distanceRate: 'Distance charge',
      totalEstimated: 'Total (estimated)',
      payCashNotice: 'Pay the exact fare in cash to your Anna after the ride.',
      suggested: 'Suggested Fare',
      total: 'Total Fare',
      base: 'Base Fare',
      distance: 'Distance Charges',
      totalCash: 'Total Cash Fare',
      tip: 'Tip',
      cancellationFee: 'Previous cancellation fee',
      nightSurcharge: 'Night surcharge',
      rainSurcharge: 'Rain surcharge',
      pickupTip: 'Pickup tip',
      waiting: 'Waiting charge ({min} min)',
      otherAdjustments: 'Other adjustments',
    },
    sos: {
      shareWhatsappSub: 'Send your live location on WhatsApp',
      shareSmsSub: 'Send your live location by SMS',
      alertContactsHint: 'Notify your saved emergency contacts',
      title: 'Emergency',
      subtitle: 'Get help quickly. Your OTP is never shared.',
      call112: 'Call 112',
      call112Sub: 'India national emergency — police, fire, ambulance',
      teamNote: 'Calling 112 or support also alerts the MyTownly safety team with your live location.',
      teamNotified: 'Calling 112 — MyTownly safety team alerted',
      callSupport: 'Call MyTownly support',
      callSupportSub: 'Talk to our team',
      alertContacts: 'Alert emergency contacts',
      alertContactsSub: 'Sends live track link (no OTP)',
      alertMsg: 'SOS from MyTownly Rides ({city}). Live trip: {track}',
      directShareMsg: 'I am taking an auto ride in {city}. Track my live location here: {track}',
      shareWhatsapp: 'Share via WhatsApp',
      shareSms: 'Share via SMS',
      noContacts: 'Add emergency contacts in Profile first',
      alerted: 'Opening alerts for {n} contact(s)',
      alertedDone: 'Contacts alerted',
      alertFailed: 'Could not send alerts',
      close: 'Close',
    },
    safety: {
      subtitle: 'Up to 3 trusted people who get your live trip link on SOS',
      savingContacts: 'Saving…',
      title: 'Emergency contacts',
      subtitle: 'Up to 3 people who get your live trip link on SOS',
      namePlaceholder: 'Name',
      phonePlaceholder: '10-digit mobile',
      add: '+ Add contact',
      remove: 'Remove',
      save: 'Save contacts',
      saved: 'Emergency contacts saved',
      invalidPhone: 'Enter a valid 10-digit number',
      nudgeTitle: 'Add emergency contacts?',
      nudgeBody: 'SOS can alert family with your live trip link — takes 20 seconds in Profile.',
      nudgeAdd: 'Add contacts',
      nudgeLater: 'Later',
    },
    track: {
      title: 'Live trip',
      loading: 'Connecting to the latest trip location…',
      retry: 'Try again',
      updatedJustNow: 'Updated just now',
      updatedAgo: 'Updated {seconds}s ago',
      liveTitle: 'Live trip',
      withCaptain: 'With {name}',
      endedTitle: 'Trip ended',
      pickup: 'Pickup',
      drop: 'Drop',
      vehicle: 'Vehicle {no}',
      viewPhoto: 'View photo',
      eta: 'Trip ~{time}',
      etaLabel: 'Estimated arrival',
      privacy: 'The live location is shown only while the trip is on. The trip PIN and phone numbers are never shared on this page.',
      titleNamed: '{name}’s auto ride',
      titleAnon: 'Auto ride',
      titleNamedIntertown: '{name}’s Intertown trip',
      titleAnonIntertown: 'Intertown trip',
      sharedFrom: 'Shared from {app} · {city}',
      live: 'Live',
      ended: 'Ended',
      minutes: '{n} min',
      stageSearching: 'Finding an auto',
      stageSearchingSub: 'Asking Annas nearby…',
      stageArriving: 'Anna arriving in',
      stageArrivingNoEta: 'Anna is on the way',
      stageArrivingSub: 'Heading to the pickup',
      arrivePickupAround: 'Reaches the pickup around {time}',
      stageArrived: 'Anna has arrived',
      stageArrivedSub: 'Waiting at the pickup',
      stageOnTrip: 'Reaching destination in',
      stageOnTripNoEta: 'Trip in progress',
      stageOnTripSub: 'Heading to the drop',
      arriveAround: 'Arrives around {time}',
      stageEnded: 'Ride completed',
      stageCancelled: 'Ride cancelled',
      stageEndedSub: 'This link stopped sharing when the trip ended.',
      completedAt: 'Ride completed at {time}',
      cancelledAt: 'Ride cancelled at {time}',
      doneThanks: 'Thanks for following the trip.',
      cancelledNote: 'This ride did not go ahead.',
      expiredTitle: 'This link has expired',
      expiredSub: 'Shared trip links stop working 12 hours after the ride ends.',
      vehicleType: 'Auto rickshaw',
      vehicleWithModel: 'Auto rickshaw · {model}',
      verified: 'Verified Anna',
      ratingAria: 'Rated {rating} out of 5',
      safetyHelp: 'Safety & help',
      helpBody: 'Worried about this ride? Call for help. For privacy, the Anna’s number isn’t shared on this page.',
      helpBodyNamed: 'Worried about {name}’s ride? Call for help. For privacy, the Anna’s number isn’t shared on this page.',
      call112: 'Call 112 · Emergency',
      callSupport: 'Call MyTownly support',
      needAuto: 'Need an auto in {city}?',
      bookOwn: 'Book your own auto in {city}',
      dropAt: 'Drop at',
      tripDetails: 'Trip details',
      hideDetails: 'Hide details',
      tripTime: 'Trip time',
      arriveBy: 'Arrive by',
      pickupBy: 'Pickup by',
      callAnna: 'Call Anna',
      annaPhone: '+91 {phone}',
      notFound: 'Link not available',
      notFoundSub: 'This trip link has expired or is not correct.',
      openApp: 'Open MyTownly Rides',
    },
    history: {
      today: 'Today',
      yesterday: 'Yesterday',
      doneToRides: 'Back to rides',
      title: 'My Rides',
      subtitle: 'Your ride history & details',
      empty: 'No rides yet',
      emptySub: 'Book your first auto from Home',
      filter_all: 'All',
      filter_completed: 'Completed',
      filter_cancelled: 'Cancelled',
      filter_active: 'Active',
      filter_past: 'Past',
      totalRides: 'Total Rides',
      completedRides: 'Completed',
      distanceCovered: 'Distance',
      co2Saved: 'CO2 Saved',
      rebook: 'Book again',
      greenFootprintTitle: 'Green Footprint',
      greenFootprintSub: "You've saved {co2} kg CO2 across your rides with MyTownly.",
      tapToOpen: 'Tap to open',
      loadOlder: 'Load older rides',
      loadingOlder: 'Loading older rides…',
      loadOlderFailed: 'Could not load older rides',
      noCompleted: 'No completed rides',
      noCancelled: 'No cancelled rides',
      noMatchMore: "Your older rides haven't been loaded yet.",
      showAll: 'Show all rides',
    },
    profile: {
      emergencyProtectionNote: 'Emergency Protection: when you trigger SOS, the MyTownly team and your contacts get instant live-location updates.',
      contactName: 'Name',
      registeredPhone: 'Phone number',
      trustedContactsMax: 'TRUSTED CONTACTS (MAX 3)',
      contactNum: 'CONTACT {n}',
      removeContact: 'Remove',
      addContact: '+ Add another trusted contact',
      title: 'Profile',
      subtitle: 'Your account & preferences',
      phone: 'Phone',
      logout: 'Log out',
      logoutSub: 'Sign out from your account',
      logoutConfirmTitle: 'Log out?',
      logoutConfirmBody: "You'll need to verify your phone number again to sign back in.",
      logoutConfirmYes: 'Log out',
      help: 'WhatsApp support',
      callSupport: 'Call support',
      editName: 'Edit name',
      editProfile: 'Edit profile',
      saveName: 'Save',
      cancelEdit: 'Cancel',
      nameSaved: 'Name updated',
      ridesDone: 'rides completed',
      ridesCompleted: '{n} rides completed',
      reviewsCount: '{n} reviews',
      joined: 'Joined {month} {year}',
      verifiedPassenger: 'Verified passenger',
      phoneVerified: 'Phone verified',
      language: 'Language',
      languageHint: 'Switch Telugu or English in one tap',
      ratingHint: 'Your passenger rating',
      appVersion: 'App Version v{version}',
      terms: 'Terms of service',
      termsSub: 'Rules, bookings and fare estimates',
      privacy: 'Privacy policy',
      privacyPolicySub: 'How we collect and protect your data',
      accountGroup: 'ACCOUNT',
      savedPlacesSub: 'Manage your saved locations',
      safetyGroup: 'SAFETY',
      safetyCenter: 'Safety center',
      safetyCenterSub: 'Safety tools, SOS, and ride security',
      emergencyContactsSub: 'Add or manage your emergency contacts',
      supportGroup: 'SUPPORT',
      helpSupport: 'Help & support',
      helpSupportSub: 'Get help with rides, payments or account',
      privacyLegalGroup: 'PRIVACY & LEGAL',
      privacyCenter: 'Privacy center',
      privacyCenterSub: 'Manage your data, consent & preferences',
      noNotifications: 'No new notifications',
      actionsGroup: 'ACTIONS',
      deleteAccount: 'Delete account',
      deleteAccountSub: 'Permanently delete your account and data',
      rateApp: 'Rate MyTownly on Play Store',
      rateAppSub: 'Share your experience with MyTownly',
    },
    app: {
      blockedTitle: 'Account blocked',
      blockedBody: 'Contact MyTownly support if you think this is a mistake.',
      skipToContent: 'Skip to content',
      errorTitle: 'Something went wrong',
      errorBody: 'We encountered a temporary issue while loading your ride screen.',
      reloadScreen: 'Reload Screen',
      returnHome: 'Return to Home',
    },
    cancel: {
      title: 'Cancel ride?',
      subtitle: 'Tell us why — helps Annas nearby',
      keep: 'Keep ride',
      confirm: 'Cancel ride',
      policyGraceTitle: 'Free cancellation window',
      policyGraceRemaining: '{time} remaining',
      policyCourtesyTitle: 'Anna is already on the way',
      policyCourtesyBody: 'This cancellation is free as a courtesy. From next time, a ₹10 fee will apply.',
      policyFeeTitle: '₹10 cancellation fee applies',
      policyFeeBody: 'A ₹10 late-cancellation fee will be added to your next ride fare.',
      reasons: {
        wait_too_long: 'Anna is taking too long',
        wrong_pickup: 'Wrong pickup location',
        changed_plans: 'Changed my plans',
        found_other: 'Booked another ride',
        captain_issue: 'Issue with the Anna',
        other: 'Something else',
      },
      reasonsSearching: {
        wait_too_long: 'Taking too long to find an auto',
      },
      whyTitle: 'Why do you want to cancel?',
      whyHint: 'Pick one. Nothing is cancelled yet.',
      keepWaiting: 'Keep waiting',
      cancelRequest: 'Cancel request',
      backToReasons: 'Back to reasons',
      reasonChip: 'Reason: {reason}',
      graceTitle: 'Free to cancel for {time}',
      graceBody: 'No charge while the Anna has just accepted.',
      noteLabel: 'Tell us what happened',
      noteOptional: '(optional)',
      notePlaceholder: 'For example: the app showed the wrong drop',
      noteHint: 'Helps us improve your next ride',
      fu: {
        waitSearchingTitle: 'Still looking for your auto',
        waitSearchingBody: 'Annas near you are busy right now. Most rides are accepted within a minute.',
        boostTitle: 'Add ₹{amount} to get one faster',
        boostSub: 'New fare ₹{fare} · more Annas see it',
        waitAssignedTitle: '{name} is on the way',
        waitAssignedBody: 'He has accepted and is coming to your pickup.',
        waitArrivedTitle: '{name} is at the pickup',
        waitArrivedBody: 'He is waiting for you right now.',
        callTitle: 'Call {name}',
        callSub: 'Ask where he is',
        pickupSearchingTitle: 'Pickup in the wrong place?',
        pickupSearchingBody: 'No one has accepted yet, so you can cancel for free and book again from the right spot.',
        rebookTitle: 'Cancel and set a new pickup',
        rebookSub: 'Your drop is kept',
        pickupRideTitle: 'Pickup in the wrong place?',
        pickupRideBody: 'Tell {name} where you are. Most pickup mix-ups are fixed with one message.',
        chatTitle: 'Message {name}',
        chatSub: 'Share your exact spot',
        issueTitle: 'Problem with the Anna?',
        issueBody: 'If you feel unsafe, call 112. Tell us what happened and we will look into it.',
        supportTitle: 'Call MyTownly support',
        supportSub: 'Our team will follow up',
        foundOtherBody: 'Thanks for telling us. It helps us get autos to you faster next time.',
        plainTitleSearching: 'Cancel this request?',
        plainTitleRide: 'Cancel this ride?',
        plainBodySearching: 'No one has accepted yet, so it costs nothing to cancel.',
        plainBodyAssigned: '{name} is already on the way to you.',
        plainBodyArrived: '{name} is waiting for you at the pickup.',
      },
    },
    rating: {
      title: 'Rate your Anna',
      subtitle: 'How was your ride?',
      commentPlaceholder: 'Say something nice (optional)',
      fareWas: 'Fare ₹{fare}',
      submit: 'Submit rating',
      skip: 'Skip for now',
      thanks: 'Thanks for your feedback!',
      thankYou: 'Thanks for your feedback!',
      rateTrip: 'Rate ride',
      quickFeedback: 'Quick feedback',
      tags: {
        polite: 'Polite Anna',
        clean: 'Clean auto',
        onTime: 'On time',
        safeDriving: 'Safe driving',
        goodRoute: 'Good route',
      },
    },
    chat: {
      title: 'Chat',
      open: 'Chat',
      empty: 'Say hi to your Anna',
      placeholder: 'Type a message…',
      send: 'Send',
      sendFailed: 'Could not send message',
    },
    pay: {
      title: 'Payment',
      cash: 'Cash',
      cashHint: 'Pay cash to the Anna when the ride ends',
    },
    places: {
      savedLocations: 'SAVED LOCATIONS',
      removeHome: 'Remove Home',
      removeWork: 'Remove Work',
      shortcutTip: 'Saved places show up as 1-tap shortcuts on your booking map for quick pickup & drop.',
      title: 'Saved places',
      home: 'Home',
      work: 'Work',
      notSet: 'Not set',
      setPickup: 'Use last pickup',
      setDrop: 'Use last drop',
      setOnMap: 'Set on map',
      setHomeMap: 'Set Home on map',
      setWorkMap: 'Set Work on map',
      confirmPin: 'Save this place',
      mapHint: 'Drop a pin on the map — no past ride needed',
      update: 'Update',
      saved: 'Place saved',
      needRide: 'Complete a ride first to set this',
      needLocation: 'Pick a location on the map',
    },
    push: {
      title: 'Turn on ride alerts',
      body: 'Get a loud alert when your Anna accepts or arrives — even if your phone is locked.',
      enable: 'Enable notifications',
      later: 'Later',
      enabled: 'Notifications on',
      denied: 'Notifications blocked — enable in phone Settings → Apps → MyTownly Rides',
      sheetTitle: 'Stay updated on your ride',
      sheetRideTitle: 'Anna accepted',
      sheetRideBody: 'Loud alert when your auto is confirmed — even if the phone is locked.',
      sheetArriveTitle: 'Anna arriving',
      sheetArriveBody: 'Know when they are near pickup so you can step out on time.',
      sheetAllow: 'Allow notifications',
      sheetLater: 'Not now',
    },
    nav: { home: 'Book', rides: 'Rides', profile: 'Profile', main: 'Main', intertown: 'Intertown' },
    riderNote: {
      add: 'Add a note for Anna',
      addHint: 'Gate, landmark, luggage… (optional)',
      addShort: 'Add',
      edit: 'Edit',
      title: 'Note for Anna',
      sub: 'Help your Anna find you. Tap any that fit, or type your own.',
      placeholder: 'e.g. Blue gate opposite the temple',
      privacy: 'Only your Anna sees this, and only after he accepts the ride.',
      save: 'Save note',
      remove: 'Remove note',
      noPhone: 'Please don’t add a phone number. Anna can call you from the app.',
      yourNote: 'Your note to Anna',
      seen: 'Anna has seen your note',
      notSeenYet: 'Waiting for Anna to open it',
      chip_gate: 'Wait at the main gate',
      chip_call: 'Call me when you arrive',
      chip_lane: 'Inside the lane, come in',
      chip_luggage: 'I have luggage',
      chip_elder: 'Elderly passenger, go slow',
      chip_kids: 'Travelling with kids',
    },
    intertown: {
      kicker: 'MYTOWNLY INTERTOWN',
      offlineTitle: 'Nearby towns, made simple.',
      offlineBody: 'Intertown bookings are being prepared for your area. Please check back soon.',
      heroTitle: 'Travel beyond your town.',
      heroBody: 'Book a trusted auto for nearby towns.',
      fromRate: 'From {rate}/km',
      fixedFare: 'Fixed fare',
      safeReturn: 'Safe return options',
      yourTrip: 'Your Intertown trip',
      cancel: 'Cancel',
      tripTypeLabel: 'Trip type',
      oneWay: 'One way',
      oneWaySub: 'Drop me there',
      returnTrip: 'Return trip',
      returnSub: 'Anna waits & brings you back',
      popularRoutes: 'Popular routes',
      pickupLabel: 'Your pickup point',
      pickupPlaceholder: 'Example: Pileru Railway Station, main entrance',
      pickupHint: 'We’ll share this with the assigned Anna only after you confirm.',
      pickupMapHint: 'Use your current location, search, or drop a pin on the map.',
      choosePickup: 'Choose pickup on map',
      customDestination: 'Choose another town',
      customDestinationHint: 'Search or choose a destination on the map',
      customLimits: '{min}–{max} km one way',
      destinationOutsideRoute: 'Choose a destination within {max} km of your pickup',
      exactSpotCta: 'Pick an exact spot in {town}',
      exactSpotChange: 'Change spot',
      useTownCentre: 'Use town centre',
      exactSpotNote: 'Your own spot in {town} · popular route fare',
      townSpotTitle: 'Choose a spot in {town}',
      townSpotSubtitle: 'Search or tap the map within {max} km of {town}. The popular route fare stays the same.',
      townSpotOutside: 'Choose a spot within {max} km of {town}',
      switchedToPopular: '{town} is a popular route, so its fixed fare applies and your spot is kept.',
      switchedToDistance: 'This trip type has no fixed fare for {town}, so it is priced by distance.',
      pickupOutsideArea: 'Pickup must be within {max} km of Pileru',
      etaPickup: 'ETA to pickup',
      etaDestination: 'ETA to destination',
      elapsed: 'On trip',
      change: 'Change',
      choose: 'Choose',
      pickupNotePlaceholder: 'Landmark, gate, or note for Anna (optional)',
      pickupMapTitle: 'Choose pickup point',
      pickerSetPickup: 'Set pickup',
      pickerSetDestination: 'Set destination',
      pickerMovePickup: 'Move map to adjust pickup',
      pickerMoveDestination: 'Move map to adjust destination',
      pickerMoving: 'Position the pin on your location',
      pickerMovingDestination: 'Position the pin on your destination',
      pickerSearchOrMove: 'Search or move the map to choose a point',
      pickerLocating: 'Getting your current location…',
      pickerCheckPin: 'Check your pickup point and adjust the pin if needed.',
      pickerAddressFailed: 'Couldn’t find this address. Try again or search for a place.',
      pickerFareAfter: 'Fare calculated after you confirm',
      pickerConfirmPickup: 'Confirm pickup',
      pickerConfirmDestination: 'Confirm destination',
      destinationMapTitle: 'Choose your destination',
      pickupMapSubtitle: 'Use your current location, search, or tap the map.',
      destinationMapSubtitle: 'Search a nearby town or tap the exact destination on the map.',
      searchLocation: 'Search a place',
      locationSearchUnavailable: 'Map search is unavailable right now. Try again shortly.',
      locationNotFound: 'We could not find that place. Try another search or tap the map.',
      currentLocationFailed: 'Could not get your current location. Choose it on the map instead.',
      closeLocationPicker: 'Close location picker',
      tapMapHint: 'Search above or tap the map to drop a pin',
      useMyLocation: 'Use my location',
      selectedPickup: 'Selected pickup',
      selectedDestination: 'Selected destination',
      tapOrSearch: 'Tap the map or search above',
      resolvingLocation: 'Finding address…',
      useThisLocation: 'Use this location',
      pleaseChoosePickup: 'Choose your pickup point on the map',
      pleaseChooseDestination: 'Choose your destination on the map',
      waitingTitle: 'Waiting is under your control',
      waitingBody: '{free} min free, then {rate} / {unit}. Maximum {max} min.',
      unitMinute: 'minute',
      unitHour: 'hour',
      calculatingFare: 'Calculating secure fare…',
      calculatingShort: 'Calculating…',
      sendingRequest: 'Sending request…',
      finishCurrentRide: 'Finish current ride first',
      requestAnna: 'Request Auto · {fare}',
      requestAnnaCta: 'Request Auto',
      pageTitle: 'Intertown',
      chipLiveTracking: 'Live trip tracking',
      chipVerifiedAnna: 'Verified Anna',
      perKm: '{rate}/km',
      fromFare: 'From {fare}',
      pickupRadiusNote: 'Pickups available within {max} km of Pileru',
      pickupShort: 'Pickup',
      destinationShort: 'Destination',
      editPickup: 'Edit pickup',
      setOnMap: 'Set on map',
      fixedFareRoute: 'Fixed fare for this route',
      fareForRoute: 'Fare for this route',
      yourFare: 'Your fare',
      fareFromDistance: 'Fare calculated from distance',
      chooseDestinationFare: 'Choose a destination to see your fare',
      fareByDistance: 'Fare by distance',
      returnShort: 'Return',
      selected: 'Selected',
      somewhereElse: 'Going somewhere else?',
      somewhereElseBody: 'Search or choose a pin on the map',
      privacyNote: 'Your pickup details are shared only after an Anna accepts.',
      choosePickupCta: 'Choose pickup',
      chooseDestinationCta: 'Choose destination',
      changePickupCta: 'Change pickup',
      changeDestinationCta: 'Change destination',
      retry: 'Try again',
      pleaseAddPickup: 'Please tell us your pickup location',
      quoteFailed: 'Could not load Intertown fare',
      routeDestinationFailed: 'Could not prepare this route destination. Please try again.',
      routeDestinationSlow: 'Finding the town is taking too long. Check your connection and try again.',
      quoteSlow: 'Checking the fare is taking too long. Check your connection and try again.',
      requestSent: 'Intertown request sent. We will confirm an Anna shortly.',
      requestFailed: 'Could not send your request',
      confirmCancel: 'Cancel this Intertown request?',
      cancelledToast: 'Intertown request cancelled',
      cancelFailed: 'Could not cancel request',
      errTooClose: 'Pickup and destination are too close. Choose a farther destination.',
      errPickupOutside: 'Your pickup is outside the Intertown area. Choose a pickup closer to town.',
      errDestinationRange: 'That destination is outside the allowed distance for this trip. Choose another.',
      errAlreadyActive: 'You already have an active Intertown request.',
      errUnavailable: 'Intertown bookings aren’t available right now. Please try again later.',
      liveMapLabel: 'Live trip map',
      trip: {
        chipFinding: 'Finding Anna',
        chipOnTheWay: 'On the way',
        chipAtPickup: 'At pickup',
        chipInProgress: 'In progress',
        chipWaiting: 'Waiting',
        chipReturning: 'Returning',
        stepFinding: 'Finding Anna',
        stepOnTheWay: 'On the way',
        stepAtPickup: 'At pickup',
        findingTitle: 'Finding an available Anna…',
        findingBody: 'Annas can see your trip and accept it. You can cancel until Anna arrives.',
        findingFor: 'Searching for {time}',
        onTheWayTitle: 'Anna is on the way',
        onTheWayEta: 'About {min} min away',
        onTheWayBody: 'Get ready at your pickup point.',
        arrivedTitle: 'Your Anna has arrived',
        arrivedBody: 'Share the Trip PIN only after boarding.',
        pinLabel: 'Trip PIN',
        etaHrMin: 'About {h} hr {m} min',
        etaHr: 'About {h} hr',
        etaMin: 'About {m} min',
        etaNow: 'Arriving now',
        arriveAround: 'Arrive around {time}',
        tripTime: 'Trip time {time}',
        etaUpdating: 'Arrival time updating',
        headingTo: 'Heading to {place}',
        delayedTitle: 'Location update delayed',
        delayedBody: 'Arrival time is unavailable for now',
        lastUpdate: 'Last update {min} min ago',
        liveNow: 'Location updated just now',
        liveStale: 'Location update delayed',
        annaDetails: 'Anna details',
        call: 'Call',
        shareDetails: 'Share details',
        shareHint: 'Share driver, plate, route and a live tracking link via WhatsApp',
        safetyHelp: 'Safety & help',
        safetyTitle: 'Safety & help',
        call112: 'Call 112',
        call112Sub: 'For emergencies. MyTownly is alerted too',
        alertTeam: 'Alert MyTownly & family',
        alertTeamSub: 'Alerts MyTownly and opens a message for you to send to family',
        alertTeamDone: 'MyTownly alerted',
        alertTeamDoneSub: 'If a message opens, press Send to notify family',
        alertShareReady: 'MyTownly alerted. Press Send to notify family.',
        callSupport: 'Call MyTownly support',
        callSupportSub: 'Get help with your trip',
        reportIssue: 'Report an issue',
        reportIssueSub: 'Tell us what happened',
        tripFare: 'Trip fare',
        baseFare: 'Base fare',
        waitingCharge: 'Waiting charge',
        soFar: '{amount} so far',
        waitingAt: 'Waiting at {place}',
        waitedFor: '{n} min waiting',
        waitFree: '{n} minutes free',
        waitThenMinute: 'Then {rate} per minute',
        waitThenHour: 'Then {rate} per hour',
        returnNote: 'Return starts when Anna begins the return leg',
        returningTitle: 'On the way back to {place}',
        returningNote: 'Live location continues on the way back',
        cancelTrip: 'Cancel trip',
        vehicleAuto: 'Auto',
      },
      mapUpdatedNow: 'Anna’s location is live',
      mapWaitingLocation: 'Connecting to Anna’s location…',
      mapLocationStale: 'Anna’s location hasn’t updated in a while',
      atPickup: 'Anna is at your pickup',
      rebookUnavailable: 'That trip can’t be booked again right now. Please choose your trip.',
      timeline: {
        title: 'Trip timeline',
        requested: 'Trip requested',
        accepted: 'Anna accepted your trip',
        arrivedPickup: 'Anna arrived at your pickup',
        started: 'Trip started',
        reachedDestination: 'Reached the destination',
        reachedAndCompleted: 'Reached the destination · trip completed',
        returnStarted: 'Return trip started',
        waited: 'Waited {min} min at the destination',
        completed: 'Trip completed',
        cancelledByYou: 'You cancelled the request',
        cancelledByAdmin: 'Cancelled by MyTownly',
        cancelledNoAnna: 'No Anna could be confirmed in time',
        cancelled: 'Request cancelled',
      },
      recenterMap: 'Recenter map',
      status: {
        pending_admin_confirmation: 'We are confirming an Anna',
        open_for_claim: 'Finding an available Anna',
        awaiting_driver_acceptance: 'Anna is reviewing your trip',
        confirmed: 'Anna confirmed — get ready',
        arrived_at_pickup: 'Anna arrived at pickup — share your Trip PIN',
        started: 'Trip started — on the way',
        at_destination: 'Anna is waiting at destination',
        returning: 'Your return ride is underway',
        completed: 'Trip completed',
        cancelled: 'Cancelled',
        updating: 'Updating',
      },
    },
    status: {
      searching: 'Searching',
      driver_assigned: 'Anna assigned',
      arrived: 'Arrived',
      started: 'On trip',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    tour: {
      next: 'Next',
      skip: 'Skip',
      done: 'Got it',
      pickup: {
        title: 'Your pickup',
        body: 'Confirm or change pickup — must be inside the {city} service area.',
      },
      where: {
        title: 'Where to?',
        body: 'Search a drop, or pin it on the map. Out of town? Set a {city} pickup first.',
      },
      shortcuts: {
        title: 'Home & Work',
        body: 'One tap to your saved places from Profile — daily routes, faster.',
      },
      book: {
        title: 'See fare & book',
        body: 'Check the estimated fare, then tap Book Auto.',
      },
      otp: {
        title: 'Share OTP with Anna',
        body: 'When your Anna arrives, share the 4-digit OTP to start the trip.',
      },
      track: {
        title: 'Track live & stay safe',
        body: 'Follow the trip on the map, share your ride, or tap SOS if you need help.',
      },
    },
    requiredUpdate: {
      versionLine: 'Version {version} or newer is required',
      updateNow: 'Update now',
      opening: 'Opening Google Play…',
      idleNote: 'This is shown only when you are not in an active ride or booking.',
    },
  },
  te: {
    network: {
      offlineTitle: 'ఆఫ్‌లైన్',
      offlineBody: 'లైవ్ అప్‌డేట్‌లు ఆగాయి. రైడ్‌ను రిఫ్రెష్ చేయడానికి కనెక్ట్ అవ్వండి.',
    },
    langToggle: 'English',
    common: {
      yes: 'అవును',
      no: 'కాదు',
      add: 'జోడించండి',
      seeAll: 'అన్నీ చూడండి',
      cancel: 'రద్దు',
      save: 'సేవ్',
      back: 'వెనుకకు',
      close: 'మూసివేయి',
      done: 'పూర్తయింది',
      ok: 'సరే',
      loading: 'లోడ్ అవుతోంది',
    },
    del: {
      title: 'ఖాతా తొలగించండి',
      subtitle: 'మీ ఖాతా మరియు వ్యక్తిగత డేటాను శాశ్వతంగా తొలగించండి',
      phoneInvalid: 'దయచేసి సరైన 10 అంకెల ఫోన్ నంబర్ నమోదు చేయండి',
      submitted: 'ఖాతా తొలగింపు అభ్యర్థన సమర్పించబడింది',
      submitFail: 'అభ్యర్థన సమర్పించలేకపోయాం. దయచేసి సపోర్ట్‌ను సంప్రదించండి.',
      valuedUser: 'విలువైన వినియోగదారు',
      received: 'అభ్యర్థన అందింది',
      receivedBody: '+91 {phone} కోసం మీ డేటా తొలగింపు అభ్యర్థన నమోదైంది. మా ప్రైవసీ అధికారి 30 రోజుల్లో తొలగింపును పూర్తి చేస్తారు.',
      trackWhatsapp: 'WhatsApp సపోర్ట్ ద్వారా ట్రాక్ చేయండి',
      irreversibleLabel: 'వెనక్కి తీసుకోలేని చర్య:',
      irreversibleBody: 'మీ ఖాతాను తొలగిస్తే మీ ప్రొఫైల్, ట్రిప్ చరిత్ర, ఎమర్జెన్సీ కాంటాక్ట్‌లు మరియు యాక్టివ్ టోకెన్‌లు శాశ్వతంగా తొలగిపోతాయి. ఖాతా యాక్సెస్ వెంటనే ఆగిపోతుంది.',
      phoneLabel: 'నమోదైన ఫోన్ నంబర్',
      phonePh: '10 అంకెల మొబైల్ నంబర్',
      reasonLabel: 'వదిలివేయడానికి కారణం',
      feedbackLabel: 'అదనపు అభిప్రాయం (ఐచ్ఛికం)',
      feedbackPh: 'మేము ఎలా మెరుగుపడగలమో చెప్పండి…',
      submitting: 'అభ్యర్థన సమర్పిస్తోంది…',
      confirm: 'నిర్ధారించి తొలగింపు అభ్యర్థించండి',
      keep: 'నా ఖాతాను ఉంచు',
      reasonNotUsing: 'ఇక MyTownly వాడటం లేదు',
      reasonPrivacy: 'ప్రైవసీ & డేటా ఆందోళనలు',
      reasonSwitched: 'మరో సేవకు మారాను',
      reasonNewAccount: 'కొత్త ఖాతా సృష్టిస్తున్నాను',
      reasonOther: 'ఇతర కారణం',
    },
    privacy: {
      title: 'ప్రైవసీ & డేటా సెంటర్',
      subtitle: 'మీ వ్యక్తిగత డేటా, ప్రాధాన్యతలు మరియు ప్రైవసీని నిర్వహించండి',
      legalHead: 'చట్టపరమైనవి & విధానాలు',
      privacyPolicy: 'ప్రైవసీ పాలసీ',
      termsOfUse: 'నిబంధనలు',
      prefsHead: 'ప్రైవసీ ప్రాధాన్యతలు',
      analyticsTitle: 'అనలిటిక్స్ నిలిపివేత',
      analyticsSub: 'వినియోగ డయాగ్నస్టిక్స్ & ఈవెంట్ టెలిమెట్రీని నిలిపివేయండి. ప్రధాన ట్రిప్ ప్రాసెసింగ్‌పై ప్రభావం ఉండదు.',
      marketingTitle: 'ప్రచార నవీకరణలు',
      marketingSub: 'అప్పుడప్పుడు రైడ్ డిస్కౌంట్‌లు & టౌన్ ప్రకటనలు పొందండి.',
      saving: 'సేవ్ అవుతోంది…',
      savePrefs: 'ప్రాధాన్యతలు సేవ్ చేయి',
      nomineeHead: 'నమ్మకమైన నామినీ',
      nomineeSub: 'మీరు చేయలేని పరిస్థితిలో మీ ఖాతా మరియు డేటాను నిర్వహించగల నమ్మకమైన కాంటాక్ట్‌ను జోడించండి.',
      nomineeNamePh: 'నామినీ పూర్తి పేరు',
      nomineePhonePh: 'నామినీ 10 అంకెల ఫోన్',
      saveNominee: 'నామినీని సేవ్ చేయి',
      dataHead: 'మీ డేటా & ఖాతా',
      downloadTitle: 'వ్యక్తిగత డేటా డౌన్‌లోడ్ చేయండి',
      downloadSub: 'మీ ప్రాసెస్ చేసిన రికార్డుల కాపీని ఎగుమతి చేయండి',
      deleteTitle: 'ఖాతా తొలగించి డేటా చెరిపివేయండి',
      deleteSub: 'మీ ప్రొఫైల్ శాశ్వత తొలగింపు',
      grievanceHead: 'ప్రైవసీ ఫిర్యాదు నమోదు చేయండి',
      grievanceSub: 'మీ డేటా ఎలా నిర్వహించబడుతుందో దానిపై సమస్య ఉందా? నేరుగా మా కంప్లయన్స్ డెస్క్‌కు సమర్పించండి.',
      grievancePh: 'మీ ఆందోళనను వివరంగా తెలియజేయండి…',
      submitting: 'సమర్పిస్తోంది…',
      submitGrievance: 'ఫిర్యాదు సమర్పించండి',
      grievanceOfficer: 'ఫిర్యాదు అధికారి:',
      whatsappDesk: 'WhatsApp డెస్క్ ›',
      prefsUpdated: 'ప్రాధాన్యతలు నవీకరించబడ్డాయి',
      prefsSaveFail: 'ప్రాధాన్యతలు సేవ్ చేయలేకపోయాం',
      nomineePhoneInvalid: 'సరైన 10 అంకెల నామినీ ఫోన్ నమోదు చేయండి',
      nomineeSaved: 'నామినీ సేవ్ చేయబడింది',
      nomineeCleared: 'నామినీ తొలగించబడింది',
      nomineeSaveFail: 'నామినీని సేవ్ చేయలేకపోయాం',
      accessSubmitted: 'యాక్సెస్ అభ్యర్థన సమర్పించబడింది',
      requestFail: 'అభ్యర్థన సమర్పించలేకపోయాం',
      grievanceMin: 'దయచేసి మీ ఆందోళనను తెలియజేయండి (కనీసం 10 అక్షరాలు)',
      grievanceSubmitted: 'ఫిర్యాదు సమర్పించబడింది',
      grievanceFail: 'ఫిర్యాదు సమర్పించలేకపోయాం',
    },
    support: {
      title: 'మేము మీకు ఎలా సహాయపడగలం?',
      forRide: 'రైడ్ #{id} కోసం సపోర్ట్',
      destination: 'గమ్యస్థానం',
      selectCategory: 'సమస్య వర్గాన్ని ఎంచుకోండి',
      describe: 'సమస్య లేదా ప్రశ్నను వివరించండి (ఐచ్ఛికం)',
      notesPh: 'మీ గమనికలు లేదా ప్రశ్నను ఇక్కడ టైప్ చేయండి…',
      continueWhatsapp: 'WhatsApp సపోర్ట్‌కు కొనసాగండి',
      attachmentHint: 'స్క్రీన్‌షాట్ లేదా ఫోటో ఉంటే వాట్సాప్ తెరిచిన తర్వాత జత చేయండి.',
      caseCreated: 'సపోర్ట్ కేస్ తెరవబడింది',
      opening: 'తెరుస్తోంది…',
      replyHint: 'మా బృందం సాధారణంగా 1 గంటలోపు స్పందిస్తుంది.',
      catFare: 'ఛార్జీ & చెల్లింపు',
      catLost: 'పోగొట్టుకున్న వస్తువు',
      catCaptain: 'డ్రైవర్ అభిప్రాయం',
      catOther: 'సాధారణ ప్రశ్న',
    },
    legal: {
      lastUpdated: 'చివరిగా నవీకరించబడింది',
    },
    splash: { loading: 'లోడ్ అవుతోంది...' },
    login: {
      tcFailed: 'Truecaller లాగిన్ విఫలమైంది:',
      tcFailedUseOtp: 'Truecaller లాగిన్ విఫలమైంది. దయచేసి OTP వాడండి.',
      tcNotUsable: 'Truecaller యాప్ తెరిచి, మీ ప్రొఫైల్ పూర్తి చేసి, మళ్ళీ ప్రయత్నించండి.',
      tcCantStart: 'Truecaller ప్రారంభం కాలేదు. కింద OTP వాడండి.',
      tcPluginMissing: 'Truecaller ప్లగిన్ లేదు — తాజా APK మళ్ళీ ఇన్‌స్టాల్ చేయండి.',
      tcUnavailable: 'Truecaller అందుబాటులో లేదు. కింద OTP వాడండి.',
      tcCantOpen: 'Truecaller తెరవలేకపోయాం. కింద OTP వాడండి.',
      signingInTruecaller: 'Truecaller ద్వారా లాగిన్ అవుతోంది…',
      verifyingProfile: 'మీ ప్రొఫైల్ తనిఖీ చేయబడుతోంది',
      tcTimeoutUseOtp: 'Truecaller చాలా సమయం తీసుకుంది. దయచేసి OTP వాడండి.',
      stillSigningIn: 'ఇంకా సైన్ ఇన్ చేస్తున్నాం — కొంత సమయం పట్టవచ్చు…',
      verifyingOtp: 'OTP తనిఖీ చేయబడుతోంది…',
      loggingIn: 'మీ ఖాతాలోకి లాగిన్ అవుతోంది',
      otpAriaLabel: 'లాగిన్ OTP',
      title: 'MyTownly Rides',
      subtitle: '{city}లో ఆటో బుక్ చేయండి',
      headline: '{city}లో ఆటో',
      headlineAccent: 'నిమిషాల్లో',
      phoneLabel: 'మొబైల్ నంబర్',
      phonePlaceholder: '10 అంకెల నంబర్',
      termsPrefix: 'కొనసాగించడం ద్వారా, మీకు 18+ సంవత్సరాలు అని నిర్ధారిస్తూ, మా',
      termsLink: 'నిబంధనలు',
      termsAnd: 'మరియు',
      privacyLink: 'గోప్యతా విధానం',
      continue: 'కొనసాగించు',
      skip: 'దాటవేయి',
      sendOtp: 'OTP పంపు',
      sendingOtp: 'OTP పంపుతున్నాం…',
      verifyOtp: 'ధృవీకరించి కొనసాగు',
      otpSent: 'OTP పంపాం!',
      resendWait: '{s} సెకన్లలో మళ్ళీ పంపండి',
      otpValidHint: 'కోడ్ 5 నిమిషాలు చెల్లుతుంది. అవసరం లేకుండా మళ్ళీ పంపవద్దు.',
      resend: 'OTP మళ్ళీ పంపు',
      nameLabel: 'మీ పేరు',
      namePlaceholder: 'అన్న మిమ్మల్ని ఏమని పిలవాలి?',
      nameInvalid: 'మీ పేరును అక్షరాలలో ఇవ్వండి (ఫోన్ నంబర్ కాదు)',
      saveName: 'కొనసాగు',
      changeNumber: 'నంబర్ మార్చు',
      invalidPhone: 'సరైన 10 అంకెల నంబర్ ఇవ్వండి',
      invalidOtp: '6 అంకెల OTP ఇవ్వండి',
      sendingOtpToast: 'OTP పంపుతోంది… వెరిఫికేషన్ స్క్రీన్ వస్తే పూర్తి చేయండి.',
      tcAppOnly: 'Truecaller MyTownly Rides యాప్‌లో మాత్రమే పని చేస్తుంది',
      resendOtpHint: 'కొత్త కోడ్ కోసం ‘OTP మళ్ళీ పంపండి’ నొక్కండి',
    },
    authErrors: {
      invalidPhone: 'చెల్లని ఫోన్ నంబర్',
      tooManyRequests: 'చాలా ప్రయత్నాలు. కొంత సేపు వేచి ఉండండి.',
      captchaFailed: 'సెక్యూరిటీ చెక్ ఫెయిల్. రిఫ్రెష్ చేసి మళ్ళీ ప్రయత్నించండి.',
      invalidOtp: 'తప్పు OTP',
      codeExpired: 'OTP గడువు ముగిసింది. మళ్ళీ పంపండి.',
      default: 'ఏదో తప్పు జరిగింది. మళ్ళీ ప్రయత్నించండి.',
      noOtpSession: 'సెషన్ ముగిసింది. కొత్త OTP అడగండి.',
      refererBlocked: 'ప్రస్తుతం లాగిన్ అందుబాటులో లేదు. కొంత సేపటి తర్వాత మళ్ళీ ప్రయత్నించండి.',
      appNotAuthorized: 'ఈ యాప్‌లో ఫోన్ లాగిన్ ప్రస్తుతం పని చేయడం లేదు. యాప్‌ను అప్‌డేట్ చేయండి లేదా తర్వాత ప్రయత్నించండి.',
      nativeOtpFailed: 'ఫోన్ లాగిన్ విఫలమైంది. మళ్ళీ ప్రయత్నించండి లేదా యాప్‌ను అప్‌డేట్ చేయండి.',
      timeout: 'SMS సమయానికి రాలేదు. సిగ్నల్ చూసి మళ్ళీ ప్రయత్నించండి.',
      phoneNotEnabled: 'ప్రస్తుతం ఫోన్ లాగిన్ అందుబాటులో లేదు. తర్వాత ప్రయత్నించండి.',
      regionNotEnabled: 'ఈ నంబర్‌కు OTP పంపలేకపోయాం. నంబర్ సరిచూసి లేదా తర్వాత మళ్ళీ ప్రయత్నించండి.',
      networkFailed: 'నెట్‌వర్క్ లోపం. కనెక్షన్ చెక్ చేయండి.',
      missingOtp: 'OTP ఇవ్వండి',
      quotaExceeded: 'SMS పరిమితి. తర్వాత ప్రయత్నించండి.',
    },
    book: {
      greetMorning: 'శుభోదయం,',
      greetAfternoon: 'శుభ మధ్యాహ్నం,',
      greetEvening: 'శుభ సాయంత్రం,',
      riderFallback: 'యూజర్',
      riderThere: 'మిత్రమా',
      menu: 'మెనూ',
      mapView: 'మ్యాప్',
      satelliteView: 'శాటిలైట్',
      toggleSatellite: 'శాటిలైట్ మ్యాప్ మార్చండి',
      showDetails: 'వివరాలు చూపించు',
      showMap: 'మ్యాప్‌కి కుదించు',
      whereTo: 'ఎక్కడికి వెళ్లాలి?',
      whereHeaded: 'మీరు ఎక్కడికి వెళ్లాలి?',
      youAreHere: 'మీరు ఇక్కడ ఉన్నారు',
      quickDestinations: 'త్వరిత గమ్యస్థానాలు',
      recentPlaces: 'ఇటీవలి స్థలాలు',
      seeAll: 'అన్నీ చూడండి',
      add: 'జోడించండి',
      currentLocation: 'మీ ప్రస్తుత లొకేషన్',
      setPickup: 'పికప్ సెట్ చేయండి',
      pickupOutside: 'సర్వీస్ ప్రాంతం వెలుపల',
      setPickupFirst: 'ముందు పికప్ సెట్ చేయండి',
      recent: 'ఇటీవలి స్థలాలు',
      trustCash: 'డ్రాప్‌లో నగదు',
      trustOtp: 'OTP తో స్టార్ట్',
      serviceRadius: 'సర్వీస్ ఏరియా',
      fareBase: 'బేస్ ఛార్జ్',
      fareDistance: 'దూరం ({km} కి.మీ × ₹{rate})',
      payOnDrop: 'డ్రాప్‌లో అన్న‌కు చెల్లించండి',
      auto: 'ఆటో',
      bookAutoFor: 'ఆటో బుక్ · ₹{fare}',
      pickup: 'పికప్',
      drop: 'డ్రాప్',
      pickupPlaceholder: '{city} లో పికప్ వెతకండి',
      useCurrentLocation: 'నా లొకేషన్ ఉపయోగించు',
      chooseTownTitle: 'మీ ఊరిని ఎంచుకోండి',
      chooseTownBody: 'మీ ఆటో బుకింగ్ కోసం ఊరిని ఎంచుకోండి.',
      townLoadTitle: 'ఊర్ల వివరాలు లోడ్ కాలేదు',
      townLoadBody: 'మీ ఇంటర్నెట్ కనెక్షన్‌ను చూసి మళ్లీ ప్రయత్నించండి. మీరు ఎంచుకున్న ఊరు సేవ్ అయి ఉంది.',
      townLoadBodyNoSaved: 'మీ ఇంటర్నెట్ కనెక్షన్‌ను చూసి మళ్లీ ప్రయత్నించండి.',
      townLoadRetry: 'మళ్లీ ప్రయత్నించండి',
      townLoadRetrying: 'ప్రయత్నిస్తోంది…',
      switchTownTitle: 'ఊరు మార్చండి',
      switchTownBody: 'పికప్ ఎంచుకున్న ఊరి సర్వీస్ ఏరియాలోనే ఉండాలి.',
      orChooseTown: 'లేదా ఊరిని ఎంచుకోండి',
      townUnavailable: 'ఈ ఊరిలో ఇంకా సర్వీస్ అందుబాటులో లేదు.',
      locationOutsideTown: 'మీ లొకేషన్ మా సర్వీస్ ఊర్ల బయట ఉంది. కొనసాగడానికి ఊరిని ఎంచుకోండి.',
      locating: 'లొకేషన్ పొందుతోంది...',
      gettingAddress: 'చిరునామా పొందుతోంది…',
      change: 'మార్చు',
      serviceArea: '{city} నుండి {km} కి.మీ లోపు రైడ్స్',
      outsideArea: '{city} నుండి {km} కి.మీ లోపు ప్రాంతాలు మాత్రమే',
      intertownRedirectHint: 'దయచేసి ఈ ప్రయాణాన్ని ఇంటర్ టౌన్ ద్వారా బుక్ చేయండి.',
      intertownRedirectCta: 'ఇంటర్ టౌన్ ద్వారా బుక్ చేయండి',
      geoDenied: 'MyTownly Rides కోసం లొకేషన్ ఆఫ్‌లో ఉంది. మీ ప్రస్తుత లొకేషన్ వాడాలంటే సెట్టింగ్స్‌లో ఆన్ చేయండి.',
      openSettings: 'సెట్టింగ్స్ తెరవండి',
      locationServicesOff: 'డివైస్ లొకేషన్ ఆఫ్‌లో ఉంది. మీ లొకేషన్ గుర్తించడానికి లొకేషన్ ఆన్ చేయండి.',
      turnOnLocation: 'లొకేషన్ ఆన్ చేయండి',
      geoTimeout: 'లొకేషన్ టైమ్ అవుట్. మళ్ళీ ప్రయత్నించండి.',
      geoUnavailable: 'లొకేషన్ కనుగొనలేకపోయాం. చిరునామా టైప్ చేయండి.',
      locationApprox: 'GPS రాలేదు — బుక్ చేసే ముందు మ్యాప్‌పై పికప్ నిర్ధారించండి',
      locationApproxHint: 'GPS సుమారుగా ఉంది — అవసరమైతే మ్యాప్‌పై సరిచేయండి',
      locationOutsideHint: 'మీరు {city} బయట ఉన్నట్టు ఉంది. {city} లో పికప్ సెట్ చేయండి — లేదా అక్కడ ఎవరికైనా బుక్ చేయండి.',
      zoneComingSoon: 'MyTownly త్వరలో {city}కి వస్తోంది! అక్కడ ఆటోలు అందుబాటులోకి వచ్చిన వెంటనే మీకు తెలియజేస్తాము.',
      forOtherTitle: 'వేరొకరి కోసం బుక్ చేస్తున్నారా?',
      forOtherFarPickup: 'పికప్ మీ లొకేషన్ నుండి {km} కి.మీ దూరం. వేరొకరి కోసం బుక్ చేస్తున్నారా?',
      forOtherOutOfTown: 'మీరు {city} బయట ఉన్నట్టు ఉంది. ఊరిలో వేరొకరి కోసం బుక్ చేస్తున్నారా?',
      forMyself: 'నా కోసం',
      forSomeoneElse: 'వేరొకరి కోసం',
      setPickupInTown: 'కొనసాగించడానికి {city} లో పికప్ సెట్ చేయండి',
      riderDetailsTitle: 'రైడర్ వివరాలు',
      riderDetailsHint: 'పికప్ వద్ద అన్న ఈ నంబర్‌కు కాల్ చేస్తారు.',
      riderName: 'రైడర్ పేరు',
      riderNamePlaceholder: 'ప్రయాణం చేసే వ్యక్తి పేరు',
      riderPhone: 'రైడర్ ఫోన్',
      riderPhonePlaceholder: '10 అంకెల నంబర్',
      riderNameRequired: 'రైడర్ పేరును అక్షరాలలో ఇవ్వండి (ఫోన్ నంబర్ కాదు)',
      riderPhoneRequired: 'సరైన 10 అంకెల ఫోన్ ఇవ్వండి',
      riderContinue: 'కొనసాగు',
      back: 'వెనక్కి',
      ridingFor: 'రైడర్: {name}',
      riderSaved: '{name} కోసం రైడ్',
      bookingPaused: 'బుకింగ్స్ తాత్కాలికంగా ఆపివేయబడ్డాయి. తర్వాత ప్రయత్నించండి.',
      pausedTitle: 'బుకింగ్‌లు నిలిపివేయబడ్డాయి',
      stoppedTitle: 'బుకింగ్‌లు అందుబాటులో లేవు',
      pausedBody: 'ప్రస్తుతం కొత్త బుకింగ్‌లు నిలిపివేయబడ్డాయి. కొద్దిసేపటి తర్వాత మళ్లీ చూడండి.',
      stoppedBody: 'MyTownly తాత్కాలికంగా అందుబాటులో లేదు. కొద్దిసేపటి తర్వాత ప్రయత్నించండి.',
      fareMinimum: 'కనీస ఛార్జీ',
      estimatedFare: 'అంచనా ఛార్జీ',
      distance: '{km} కి.మీ · ~{min} నిమి',
      bookRide: 'ఆటో బుక్ చేయి',
      searching: 'ఆటో కోసం వెతుకుతోంది...',
      cash: 'అన్న‌కు నగదు చెల్లించండి',
      billingError: 'ప్రస్తుతం మ్యాప్ అందుబాటులో లేదు. కొంత సేపటి తర్వాత మళ్ళీ ప్రయత్నించండి.',
      mapsError: 'మ్యాప్ లోడ్ కాలేదు. కనెక్షన్ చూసి మళ్ళీ ప్రయత్నించండి.',
      routeError: 'మార్గం లెక్కించలేకపోయాం',
      placeError: 'ఆ స్థలం లోడ్ కాలేదు. మళ్ళీ ప్రయత్నించండి లేదా “మ్యాప్‌లో లొకేట్” ఉపయోగించండి.',
      locationError: 'లొకేషన్ పొందలేకపోయాం',
      bookError: 'రైడ్ బుక్ చేయలేకపోయాం',
      errZoneInactive: 'ఈ ప్రాంతంలో MyTownly ఇంకా అందుబాటులో లేదు.',
      errTooClose: 'పికప్, డ్రాప్ చాలా దగ్గరగా ఉన్నాయి. ఇంకొంచెం దూరంలో డ్రాప్ ఎంచుకోండి.',
      errTooLong: 'ఈ ప్రయాణం లోకల్ ఆటోకు చాలా దూరం. ఎక్కువ దూరానికి ఇంటర్ టౌన్ ప్రయత్నించండి.',
      errTooMany: 'చాలాసార్లు ప్రయత్నించారు. ఒక నిమిషం ఆగి మళ్ళీ బుక్ చేయండి.',
      errBlocked: 'మీ ఖాతాతో ప్రస్తుతం రైడ్ బుక్ చేయలేరు. దయచేసి సపోర్ట్‌ను సంప్రదించండి.',
      errTipChanged: 'సమీపంలోని ఆటోలు మారాయి, కాబట్టి టిప్ ఎంపికలు మారాయి. దయచేసి చూసుకుని మళ్ళీ బుక్ చేయండి.',
      errWaitingTermsChanged: 'వెయిటింగ్ ఛార్జీ నిబంధనలు ఇప్పుడే మారాయి. దయచేసి కొత్త నిబంధనలు చూసి మళ్ళీ బుక్ చేయండి.',
      waitingTerms: 'వెయిటింగ్ ఛార్జీ: {free} నిమిషాలు ఉచితం, తర్వాత ₹{rate}/నిమిషం, మీ అన్నకు చెల్లించాలి',
      pickupEta: '{min} నిమిషాల దూరంలో',
      dropAt: 'డ్రాప్ {time}',
      seatsLabel: 'సీట్లు {n}',
      pickupMid: 'దగ్గరి ఆటో {km} కి.మీ. దూరంలో ఉంది (~{min} నిమి.)',
      pickupFar: 'ఆటోలు దూరంగా ఉన్నాయి ({km} కి.మీ., ~{min} నిమి.)',
      pickupNone: 'ప్రస్తుతం సమీపంలో ఆటోలు లేవు',
      tipHintMid: 'ఆటో త్వరగా రావాలంటే టిప్ జోడించండి',
      tipHintFar: 'సూచన: ఆటో త్వరగా రావడానికి టిప్ జోడించండి',
      tipHintNone: 'మీరు ఇప్పటికీ బుక్ చేయవచ్చు — మేము వెతుకుతూనే ఉంటాం. టిప్ ఇస్తే దూరంలోని ఆటోలకూ చేరుతుంది.',
      tipHintOffline: 'ప్రస్తుతం ఏ ఆటో ఆన్‌లైన్‌లో లేదు. మీరు ఇప్పటికీ బుక్ చేయవచ్చు, మేము వెతుకుతూనే ఉంటాం.',
      pickupTipChosen: 'పికప్ టిప్ +₹{amount} జోడించబడింది',
      callSupport: 'సపోర్ట్‌కు కాల్ చేయండి',
      sharedOutside: '📍 షేర్ చేసిన గమ్యం సేవా ప్రాంతానికి బయట ఉంది',
      sharedDestSet: '📍 గమ్యం సెట్ చేశాం: {name}',
      sharedLocation: 'షేర్ చేసిన ప్రదేశం',
      activeRideExists: 'మీకు ఇప్పటికే యాక్టివ్ రైడ్ ఉంది',
      hi: 'హాయ్ {name}!',
      readyToGo: 'ఎక్కడికి వెళ్లాలనుకుంటున్నారు?',
      whereGoing: 'ఎక్కడికి వెళ్లాలి?',
      pickupFrom: 'పికప్',
      planTrip: 'మీ ప్రయాణం ప్లాన్ చేయండి',
      confirmRide: 'రైడ్ నిర్ధారించండి',
      editTrip: 'లొకేషన్లు మార్చు',
      swap: 'పికప్ & డ్రాప్ మార్చు',
      recenter: 'నా లొకేషన్',
      calculating: 'ఛార్జీ లెక్కిస్తోంది…',
      finishIntertownFirst: 'ముందు మీ Intertown ట్రిప్ పూర్తి చేయండి',
      locateOnMap: 'మ్యాప్‌పై సెట్ చేయి',
      pinPickupTitle: 'మ్యాప్‌పై పికప్ సెట్ చేయండి',
      pinDropTitle: 'మ్యాప్‌పై డ్రాప్ సెట్ చేయండి',
      pinHint: 'మ్యాప్ కదపండి — పిన్ మధ్యలో ఉంటుంది',
      pinAreaHint: '{name} పెద్ద ప్రాంతం. మీరు దిగాల్సిన సరైన చోటుకు పిన్‌ను కదపండి.',
      pinConfirmPickup: 'పికప్ నిర్ధారించు',
      pinConfirmDrop: 'డ్రాప్ నిర్ధారించు',
      pickupCheckTitle: 'ఇదేనా మీ పికప్ స్థలం?',
      pickupCheckHint: 'అన్న ఈ పిన్ దగ్గరకే వస్తారు. మ్యాప్ కదిపి పిన్‌ను మీ గేటు దగ్గర పెట్టండి.',
      pickupCheckMovedHint: 'పిన్ మార్చారు, ఛార్జీ మారవచ్చు. బుక్ చేసే ముందు కొత్త ఛార్జీ చూపిస్తాం.',
      pickupCheckBook: 'నిర్ధారించి బుక్ చేయి · ₹{fare}',
      pickupMovedFare: 'పికప్ మార్చారు, ఛార్జీ ₹{from} నుండి ₹{to} కి మారింది.',
      pickupMovedSameFare: 'పికప్ మార్చారు. ఛార్జీ అదే.',
      pinLoading: 'చిరునామా తెస్తోంది…',
      pinApprox: 'ఎంచుకున్న స్థలం',
      adjustOnMap: 'మ్యాప్‌పై సరిచేయి',
      shortcutHome: 'ఇల్లు',
      shortcutWork: 'ఆఫీస్',
      shortcutBusStand: 'బస్ స్టాండ్',
      shortcutCrossRoad: 'క్రాస్ రోడ్',
      shortcutMarketYard: 'మార్కెట్ యార్డ్',
      shortcutRailway: 'రైల్వే స్టేషన్',
      shortcutHospital: 'ప్రభుత్వ ఆసుపత్రి',
      saveHomeTitle: 'ఇంటి అడ్రస్ సేవ్ చేయండి',
      saveWorkTitle: 'ఆఫీస్ అడ్రస్ సేవ్ చేయండి',
      savedHome: 'ఇంటి అడ్రస్ సేవ్ చేయబడింది',
      savedWork: 'ఆఫీస్ అడ్రస్ సేవ్ చేయబడింది',
      yourLocation: 'మీ స్థానం',
      pinpointingPickup: 'మీ స్థానం',
      tapToSetPickup: 'పికప్ లొకేషన్ సెట్ చేయడానికి తాకండి',
      selectedPoi: 'ఎంచుకున్నది',
    },
    active: {
      tripPin: 'ట్రిప్ PIN',
      tripPinHint: 'ఈ PIN మీ అన్న‌కు చెప్పండి',
      viewAnnaProfile: 'అన్న ప్రొఫైల్ చూడండి',
      tripDetails: 'ట్రిప్ వివరాలు',
      rideProgress: 'రైడ్ పురోగతి',
      recenterMap: 'మ్యాప్ మధ్యలో పెట్టు',
      searchingNearby: 'సమీపంలో అన్నల కోసం వెతుకుతోంది…',
      nightShieldTitle: 'ఆలస్యంగా ప్రయాణమా?',
      nightShieldBody: 'ఆలస్యమైంది. {city}లో మీకు నమ్మకమైన వారితో మీ లైవ్ ట్రిప్ షేర్ చేయండి.',
      nightShieldShare: 'నా లైవ్ ట్రిప్ షేర్ చేయి',
      nightShieldOk: 'నేను బాగానే ఉన్నాను',
      etaArriving: '{min} నిమిషాల్లో వస్తారు',
      etaArrived: 'మీ అన్న వచ్చారు',
      etaHeadingMin: 'గమ్యస్థానానికి {min} నిమిషాలు',
      etaHeading: 'మీ గమ్యస్థానానికి వెళుతోంది',
      etaSubAssigned: 'మిమ్మల్ని ఎక్కించుకోవడానికి అన్న వస్తున్నారు',
      etaSubArrived: 'పికప్ పాయింట్ దగ్గర మీ అన్న‌ను కలవండి',
      etaSubStarted: 'హాయిగా కూర్చుని ప్రయాణం ఆస్వాదించండి',
      captainConnecting: 'మీ అన్న మీ వద్దకు వస్తున్నారు',
      captainConnectingSub: 'పూర్తి వివరాల కోసం మళ్ళీ కనెక్ట్ అవుతోంది…',
      cashPaid: 'మీరు ₹{fare} నగదు చెల్లించారు',
      rateYourRideWith: 'మీ రైడ్‌కి రేటింగ్ ఇవ్వండి —',
      rateStars: '{n} నక్షత్రాలు ఇవ్వండి',
      ratingSubmitted: '{stars} ★ ఇచ్చారు — ధన్యవాదాలు!',
      sentiment1: 'చాలా చెడు',
      sentiment2: 'బాగోలేదు',
      sentiment3: 'ఫర్వాలేదు',
      sentiment4: 'బాగుంది',
      sentiment5: 'అద్భుతం!',
      quickChecks: 'త్వరిత తనిఖీలు',
      qFareCorrect: 'ఛార్జీ సరిగ్గా ఉందా?',
      qRouteGood: 'అన్న మంచి మార్గంలో వెళ్లారా?',
      qExtraMoney: 'అదనపు డబ్బు అడిగారా?',
      submitRatingFinish: 'సమర్పించి ముగించు',
      bookAnother: 'మరో రైడ్ బుక్ చేయండి',
      viewReceipt: 'రసీదు చూడండి',
      safetyFirst: 'భద్రతే మొదటిది',
      safetyShareSub: 'మీ లైవ్ ట్రిప్ స్థితిని కుటుంబం లేదా స్నేహితులతో షేర్ చేయండి',
      shareLiveTrip: 'లైవ్ ట్రిప్ షేర్ చేయి',
      cancelChargesMayApply: 'రద్దు ఛార్జీలు వర్తించవచ్చు',
      boostFailed: 'ఛార్జీ పెంచలేకపోయాం. మళ్ళీ ప్రయత్నించండి.',
      title: 'మీ రైడ్',
      findingCaptain: 'సమీపంలో ఆటోలు వెతుకుతోంది',
      nearbyShort: '{count} సమీపంలో',
      nearbyShortOne: '1 సమీపంలో',
      lookingNearbyShort: 'వెతుకుతోంది…',
      captainAssigned: 'అన్న దొరికారు! మీ వద్దకు వస్తున్నారు',
      captainOnWay: 'అన్న వస్తున్నారు',
      annaCancelledSearching: '{name} రద్దు చేశారు. మీ కోసం మరొక అన్నాని వెతుకుతున్నాం...',
      searching: 'సమీపంలో ఆటో కోసం వెతుకుతోంది',
      assigned: 'అన్న వస్తున్నారు',
      arrived: 'అన్న వచ్చారు',
      started: 'ప్రయాణం జరుగుతోంది',
      tripStarted: 'ప్రయాణం ప్రారంభమైంది! గమ్యస్థానానికి వెళ్తున్నారు',
      tripStartedToast: 'ప్రయాణం ప్రారంభమైంది! గమ్యస్థానానికి వెళ్తున్నారు',
      enjoyRide: 'మీ ప్రయాణం ఆనందించండి!',
      completed: 'రైడ్ పూర్తి',
      cancel: 'రద్దు చేయి',
      cancelConfirm: 'ఈ రైడ్ రద్దు చేయాలా?',
      shareOtp: 'ప్రయాణం ప్రారంభించడానికి అన్నకు OTP చెప్పండి',
      shareOtpWithCaptain: 'అన్నకు OTP చెప్పండి',
      otp: 'ప్రయాణం మొదలు పెట్టడానికి ఈ OTP అన్న‌కు చెప్పండి',
      driver: 'అన్న',
      vehicle: 'వాహనం',
      payCash: 'అన్న‌కు ₹{fare} నగదు చెల్లించండి',
      stepSearch: 'వెతుకుతోంది',
      stepAssigned: 'వస్తున్నారు',
      stepArrived: 'వచ్చారు',
      stepTrip: 'ప్రయాణంలో',
      stepDone: 'పూర్తి',
      eta: 'ప్రయాణ సమయం ~{time}',
      nearbyCaptains: '{count} ఆటోలు సమీపంలో',
      nearbyCaptainsOne: '1 ఆటో సమీపంలో',
      noCaptainsNearby: 'సమీపంలో ఆటోలు వెతుకుతోంది…',
      notifyingNearby: '{count} ఆటోలు సమీపంలో',
      notifyingNearbyOne: '1 ఆటో సమీపంలో',
      expandingSearch: 'శోధన విస్తరిస్తోంది…',
      ringNear: 'మీకు దగ్గరి ఆటోలను అడుగుతోంది…',
      ringWider: 'కాస్త దూరంగా వెతుకుతోంది…',
      ringArea: '{city} అంతా వెతుకుతోంది…',
      searchTimeout: 'సమీపంలో ఆటోలు లేవు',
      searchTimeoutHint: 'వేచి ఉండండి, మళ్లీ బుక్ చేయండి, లేదా సపోర్ట్‌కు కాల్ చేయండి',
      noCaptain: 'ఇప్పుడు ఆటోలు అందుబాటులో లేవు',
      noCaptainHint: 'ఒక నిమిషం తర్వాత మళ్లీ బుక్ చేయండి — లేదా వేచి ఉండండి',
      noCaptainHintDistance: 'సమీప అన్న ప్రస్తుతం {km} కి.మీ దూరంలో ఉన్నారు — కింద ఛార్జీ పెంచి చూడండి, లేదా వేచి ఉండండి',
      doneToRides: 'రైడ్స్‌కు వెళ్ళు',
      ratePrompt: 'రైడ్ ఎలా ఉంది?',
      keepWaiting: 'వేచి ఉండండి',
      bookAgain: 'మళ్లీ బుక్ చేయండి',
      callSupport: 'సపోర్ట్‌కు కాల్',
      pickupEta: 'అన్న ~{min} నిమిషాల్లో',
      captainAboutToArrive: 'అన్న చేరబోతున్నారు!',
      captainAboutToArriveSub: 'పికప్ పాయింట్ వద్ద సిద్ధంగా ఉండండి',
      pickupEtaShort: 'పికప్‌కు',
      dropEtaShort: 'డ్రాప్‌కు',
      etaMin: 'నిమి',
      liveTracking: 'లైవ్ ట్రాకింగ్',
      tripTime: 'ప్రయాణ సమయం ~{time}',
      otpReady: 'అన్న కోసం OTP',
      otpValidUntil: 'కాపీ చేయడానికి నొక్కండి — ప్రయాణం మొదలుపెట్టడానికి అన్న‌కు చెప్పండి',
      otpCopied: 'OTP కాపీ అయింది',
      distanceTime: 'దూరం & సమయం',
      fareBreakdown: 'ఛార్జ్ వివరాలు',
      fareBreakdownLine: 'బేస్ ₹{base} + దూరం ₹{dist} = ₹{total}',
      matchAsk: 'ఆటో {no} సరిపోతుందా?',
      boostTitle: '⚡ ఆటో త్వరగా రావడానికి',
      boostHint: 'ఆటో త్వరగా రావడానికి కొద్దిగా టిప్ జోడించండి',
      boostHintDistance: 'సమీప అన్న {km} కి.మీ దూరంలో ఉన్నారు — త్వరగా చేరుకోవడానికి ఛార్జీ పెంచండి',
      boostNoCaptains: 'ప్రస్తుతం అన్నలు ఎవరూ ఆన్‌లైన్‌లో లేరు — ఇప్పుడు టిప్ పెంచినా ప్రయోజనం ఉండదు. ఒకరు అందుబాటులోకి రాగానే మీకు తెలియజేస్తాము.',
      boostChip: '+₹{amount}',
      boostApplied: 'ఛార్జ్ +₹{tip} పెంచబడింది (మొత్తం: ₹{fare})',
      boostSuccess: '+₹{amount} పెంచబడింది! డ్రైవర్లకు తెలియజేస్తున్నాము.',
      boostTooLate: 'మీ రైడ్‌ను ఇప్పటికే ఒక అన్న తీసుకున్నారు, కాబట్టి ఇప్పుడు ఛార్జీ పెంచలేము.',
      boostWait: 'మళ్ళీ పెంచే ముందు కొన్ని సెకన్లు ఆగండి.',
      boostMax: 'గరిష్ట టిప్ పరిమితి చేరింది (₹{cap})',
      matchYes: 'అవును, సరిపోతుంది',
      matchNo: 'కాదు, తప్పు ఆటో',
      matchThanksYes: 'ధన్యవాదాలు — రికార్డ్ అయింది',
      matchThanksNo: 'ధన్యవాదాలు — మేము సమీక్షిస్తాం',
      matchRecordedYes: 'ఆటో నంబర్ సరిపోయిందని నిర్ధారించారు',
      matchRecordedNo: 'మ్యాచ్ కాలేదు — టీమ్ సమీక్షించవచ్చు',
      call: 'కాల్',
      callCaptain: 'అన్న‌కు కాల్',
      share: 'షేర్',
      navigate: 'నావిగేషన్ తెరవండి',
      navUnavailable: 'నావిగేషన్‌కు లొకేషన్ లేదు',
      sos: 'SOS',
      shareTripMsg: 'MyTownly Ride · {status}\nగమ్యం: {dest}\nఅన్న: {name}\nఆటో: {vehicle}\nఛార్జ్: ₹{fare}\nలైవ్ ట్రాక్: {link}',
      sharePreparing: 'లైవ్ ట్రాక్ లింక్ సిద్ధం చేస్తోంది…',
      shareReady: 'లింక్ రెడీ — మళ్లీ Share నొక్కండి',
      shareUnavailable: 'షేర్ చేయలేకపోయాం',
      sosUnavailable: 'సపోర్ట్ నంబర్ లేదు',
      rideNotFound: 'రైడ్ కనబడలేదు',
      rideNotFoundSub: 'ఈ ట్రిప్ ముగిసి ఉండవచ్చు లేదా లింక్ తప్పు.',
      showMap: 'మ్యాప్ చూడండి',
      showDetails: 'వివరాలు చూడండి',
      fitMap: 'మ్యాప్‌లో అందరినీ చూపు',
      tripsCount: '{n} ట్రిప్స్',
      safetyCheckTitle: 'ఎక్కే ముందు సేఫ్టీ చెక్',
      safetyCheckSub: 'OTP చెప్పే ముందు ఆటో నంబర్ {no} మరియు అన్న {name} సరిపోయాయో లేదో సరిచూసుకోండి.',
      captainVerified: 'అన్న వెరిఫై అయ్యారు',
      unexpectedStopTitle: 'మీ ప్రయాణం అంతా బాగుందా?',
      unexpectedStopSub: 'మీ వాహనం 5 నిమిషాలకు పైగా ఒకే చోట ఆగి ఉన్నట్లు గమనించాము.',
      locationStale: 'అన్న లొకేషన్ కొంతసేపటి నుండి అప్‌డేట్ కాలేదు',
      imSafe: 'నేను సేఫ్‌గానే ఉన్నాను',
      keepWaitingNotifying: 'మళ్లీ సమీపంలోని అన్నల కోసం వెతుకుతోంది…',
      searchExpiredToast: 'సమీపంలో అన్న అందుబాటులో లేరు. దయచేసి మళ్లీ బుక్ చేయండి.',
      waitingTitle: 'మీ అన్న వేచి ఉన్నారు',
      waitingFreeLeft: 'ఉచిత వెయిటింగ్ సమయం మిగిలింది {time}',
      waitingSoFar: 'ఇప్పటివరకు ₹{amount} వెయిటింగ్ · {time}',
      waitingTerms: '{free} నిమిషాలు ఉచితం, తర్వాత ₹{rate}/నిమిషం',
      waitingHint: 'మీరు PIN చెప్పి ట్రిప్ మొదలైనప్పుడు ఛార్జీ ఆగుతుంది.',
    },
    details: {
      receiptTitle: 'రైడ్ వివరాలు',
      rideLoading: 'రైడ్ వివరాలు లోడ్ అవుతున్నాయి…',
      rideLoadFailed: 'ఈ రైడ్ వివరాలు లోడ్ కాలేదు. మళ్లీ ప్రయత్నించండి.',
      retry: 'మళ్లీ ప్రయత్నించండి',
      receiptAutoRide: 'ఆటో రైడ్',
      receiptFareBreakdown: 'ఛార్జీల వివరాలు',
      receiptRebook: 'ఈ మార్గాన్ని మళ్లీ బుక్ చేయండి',
      receiptKm: 'కి.మీ',
      receiptMin: 'నిమి',
      dropoff: 'డ్రాప్-ఆఫ్',
      paidInCash: 'నగదు చెల్లించారు',
      paymentReceived: 'చెల్లింపు అందింది',
      paymentPending: 'చెల్లింపు పెండింగ్‌లో ఉంది',
      totalPaid: 'మొత్తం చెల్లింపు',
      paid: 'చెల్లించారు',
      distanceFare: 'దూరం ఛార్జీ',
      distanceCalculation: '{km} కి.మీ × ₹{rate}/కి.మీ',
      distanceRate: 'రేటు: ₹{rate}/కి.మీ',
      rounded: 'రౌండ్ చేసిన మొత్తం',
      routeMap: 'పికప్ మరియు డ్రాప్ మ్యాప్',
      mapLoading: 'మ్యాప్ లోడ్ అవుతోంది…',
      mapUnavailable: 'మ్యాప్ అందుబాటులో లేదు',
      receiptActions: 'రసీదు మరియు సహాయం',
      copyId: 'రైడ్ ID కాపీ చేయండి',
      idCopied: 'రైడ్ ID కాపీ అయింది',
      copyFailed: 'రైడ్ ID కాపీ చేయలేకపోయాం',
      yourRating: 'మీ రేటింగ్: {rating} స్టార్లు',
      estimatedShort: '(అంచనా)',
      minsLabel: 'నిమి',
      kmsLabel: 'కి.మీ',
      autoRide: 'ఆటో రైడ్',
      rideId: 'రైడ్ ID',
      tapAway: 'ఒక్క ట్యాప్ దూరంలో ఉన్నాం',
      title: 'రైడ్ వివరాలు',
      addresses: 'చిరునామా వివరాలు',
      driverCard: 'డ్రైవర్ & వాహనం',
      fareSummary: 'ఛార్జీల వివరాలు',
      rebookRoute: 'మళ్లీ బుక్ చేయండి',
      needHelp: 'సహాయం కావాలా / కాల్ చేయండి',
      unavailable: 'అందుబాటులో లేదు',
      fareBreakdownUnavailable: 'ఈ పాత రైడ్‌కు ఛార్జీ విభజన నమోదు కాలేదు.',
      shareReceipt: 'రసీదు షేర్ చేయండి',
      reportIssue: 'సమస్యను నివేదించండి',
      cashCollected: 'నగదు సేకరించినట్లు నమోదు అయింది',
      cashDue: 'రైడ్ తర్వాత నగదు చెల్లించాలి',
    },
    driver: {
      annaProfile: 'అన్న ప్రొఫైల్',
      verifiedPartner: 'ధృవీకరించబడిన భాగస్వామి',
      verifiedPartnerShort: 'ధృవీకరించబడింది',
      communityCaptain: 'నమోదైన అన్న',
      newRating: 'కొత్తది',
      newCaptain: 'కొత్త అన్న',
      ratingWithCount: 'రేటింగ్ ({n})',
      rating: 'రేటింగ్',
      tripsCount: '{n} ట్రిప్‌లు',
      experience: 'అనుభవం',
      operatingArea: 'సేవా ప్రాంతం',
      vehicleDetails: 'వాహన వివరాలు',
      autoFallback: 'ఆటో',
      credentials: 'ధృవీకరించబడిన వివరాలు',
      credIdentity: 'గుర్తింపు ధృవీకరించబడింది',
      credIdentitySub: 'ప్రభుత్వ ID తనిఖీ చేయబడింది',
      credAuto: 'ఆటో ధృవీకరించబడింది',
      credAutoSub: '{city}లో నమోదైంది',
      credCash: 'నగదు చెల్లింపులు',
      credCashSub: 'రైడ్ తర్వాత నగదుతో చెల్లించండి',
      verified: 'ధృవీకరించబడిన అన్న',
      viewPhoto: 'ఫోటో చూడండి',
      safetyTitle: 'ఆటో భద్రతా నివేదిక',
      safetySubtitle: 'మీ భద్రతే మాకు ముఖ్యం ❤️',
      safetyAudited: 'RTA తనిఖీ చేయబడింది',
      safetyPassed: '100% ఉత్తీర్ణత',
      safetyRc: 'RC స్థితి',
      safetyInsurance: 'వాహన ఇన్సూరెన్స్',
      safetyDrivingLicence: 'డ్రైవింగ్ లైసెన్స్ (DL)',
      safetyValidUpto: 'చెల్లుబాటు తేదీ',
      safetyStatus: 'స్థితి',
      safetyValid: 'చెల్లుబాటులో ఉంది',
      safetyPending: 'తనిఖీ పెండింగ్‌లో ఉంది',
      safetyExpired: 'గడువు ముగిసింది',
      safetyUnavailable: 'ఇంకా ప్రచురించలేదు',
      safetyFooter: 'ఈ ఆటో అన్ని రవాణా & భద్రతా ప్రమాణాలకు అనుగుణంగా ఉంది',
      safetyFooterPartial: 'చాలా భద్రతా వివరాలు ధృవీకరించబడ్డాయి; కొన్ని తనిఖీ అవసరం',
      safetyFooterUnavailable: 'ఈ ఆటో భద్రతా వివరాలు ఇంకా ప్రచురించలేదు',
    },
    fare: {
      distanceRate: 'దూర ఛార్జీ',
      totalEstimated: 'మొత్తం (అంచనా)',
      payCashNotice: 'రైడ్ తర్వాత సరైన ఛార్జీని మీ అన్న‌కు నగదుతో చెల్లించండి.',
      suggested: 'సూచించిన ఛార్జీ',
      total: 'మొత్తం ఛార్జీ',
      base: 'మూల ఛార్జీ',
      distance: 'దూరం ఛార్జీలు',
      totalCash: 'మొత్తం నగదు ఛార్జీ',
      tip: 'టిప్',
      cancellationFee: 'మునుపటి రద్దు రుసుము',
      nightSurcharge: 'రాత్రి అదనపు ఛార్జీ',
      rainSurcharge: 'వర్ష అదనపు ఛార్జీ',
      pickupTip: 'పికప్ టిప్',
      waiting: 'వెయిటింగ్ ఛార్జీ ({min} నిమి.)',
      otherAdjustments: 'ఇతర సర్దుబాట్లు',
    },
    sos: {
      shareWhatsappSub: 'మీ లైవ్ లొకేషన్‌ను WhatsAppలో పంపండి',
      shareSmsSub: 'మీ లైవ్ లొకేషన్‌ను SMS ద్వారా పంపండి',
      alertContactsHint: 'మీ సేవ్ చేసిన ఎమర్జెన్సీ కాంటాక్ట్‌లకు తెలియజేయండి',
      title: 'ఎమర్జెన్సీ',
      subtitle: 'త్వరగా సహాయం పొందండి. OTP ఎప్పుడూ షేర్ కాదు.',
      call112: '112 కు కాల్',
      call112Sub: 'భారత జాతీయ ఎమర్జెన్సీ — పోలీస్, ఫైర్, అంబులెన్స్',
      teamNote: '112 లేదా సపోర్ట్‌కు కాల్ చేస్తే MyTownly సేఫ్టీ టీమ్‌కు మీ లైవ్ లొకేషన్‌తో అలర్ట్ వెళ్తుంది.',
      teamNotified: '112 కు కాల్ చేస్తోంది — MyTownly సేఫ్టీ టీమ్‌కు అలర్ట్ అయింది',
      callSupport: 'MyTownly సపోర్ట్ కాల్',
      callSupportSub: 'మా టీమ్‌తో మాట్లాడండి',
      alertContacts: 'ఎమర్జెన్సీ కాంటాక్ట్స్‌కు అలర్ట్',
      alertContactsSub: 'లైవ్ ట్రాక్ లింక్ పంపుతుంది (OTP లేదు)',
      alertMsg: 'MyTownly Rides SOS ({city}). లైవ్ ట్రిప్: {track}',
      directShareMsg: 'నేను {city}లో ఆటో ప్రయాణంలో ఉన్నాను. నా లైవ్ లొకేషన్‌ను ఇక్కడ చూడండి: {track}',
      shareWhatsapp: 'వాట్సాప్ ద్వారా షేర్ చేయండి',
      shareSms: 'SMS ద్వారా షేర్ చేయండి',
      noContacts: 'ముందు ప్రొఫైల్‌లో ఎమర్జెన్సీ కాంటాక్ట్స్ జోడించండి',
      alerted: '{n} కాంటాక్ట్(ల)కు అలర్ట్ తెరుస్తోంది',
      alertedDone: 'కాంటాక్ట్స్‌కు అలర్ట్ అయింది',
      alertFailed: 'అలర్ట్ పంపలేకపోయాం',
      close: 'మూసివేయి',
    },
    safety: {
      subtitle: 'SOS నొక్కినప్పుడు మీ లైవ్ ట్రిప్ లింక్ అందుకునే 3 నమ్మకమైన వ్యక్తులు వరకు',
      savingContacts: 'సేవ్ అవుతోంది…',
      title: 'ఎమర్జెన్సీ కాంటాక్ట్స్',
      subtitle: 'SOS లో లైవ్ ట్రాక్ పొందే 3 మంది వరకు',
      namePlaceholder: 'పేరు',
      phonePlaceholder: '10 అంకెల మొబైల్',
      add: '+ కాంటాక్ట్ జోడించు',
      remove: 'తొలగించు',
      save: 'సేవ్ చేయి',
      saved: 'ఎమర్జెన్సీ కాంటాక్ట్స్ సేవ్ అయ్యాయి',
      invalidPhone: 'సరైన 10 అంకెల నంబర్ ఇవ్వండి',
      nudgeTitle: 'ఎమర్జెన్సీ కాంటాక్ట్స్ జోడించాలా?',
      nudgeBody: 'SOS కుటుంబానికి లైవ్ ట్రిప్ లింక్ పంపుతుంది — ప్రొఫైల్‌లో 20 సెకన్లు',
      nudgeAdd: 'కాంటాక్ట్స్ జోడించు',
      nudgeLater: 'తర్వాత',
    },
    track: {
      title: 'లైవ్ ట్రిప్',
      loading: 'తాజా ట్రిప్ లొకేషన్‌కు కనెక్ట్ అవుతోంది…',
      retry: 'మళ్లీ ప్రయత్నించండి',
      updatedJustNow: 'ఇప్పుడే అప్‌డేట్ అయింది',
      updatedAgo: '{seconds} సెకన్ల క్రితం అప్‌డేట్ అయింది',
      liveTitle: 'లైవ్ ట్రిప్',
      withCaptain: '{name} తో',
      endedTitle: 'ట్రిప్ ముగిసింది',
      pickup: 'పికప్',
      drop: 'డ్రాప్',
      vehicle: 'వాహనం {no}',
      viewPhoto: 'ఫోటో చూడండి',
      eta: 'ప్రయాణం ~{time}',
      etaLabel: 'అంచనా సమయం',
      privacy: 'ట్రిప్ జరుగుతున్నప్పుడు మాత్రమే లైవ్ లొకేషన్ కనిపిస్తుంది. ట్రిప్ PIN, ఫోన్ నంబర్లు ఈ పేజీలో ఎప్పుడూ షేర్ కావు.',
      titleNamed: '{name} ఆటో రైడ్',
      titleAnon: 'ఆటో రైడ్',
      titleNamedIntertown: '{name} ఇంటర్ టౌన్ ట్రిప్',
      titleAnonIntertown: 'ఇంటర్ టౌన్ ట్రిప్',
      sharedFrom: '{app} నుండి షేర్ చేయబడింది · {city}',
      live: 'లైవ్',
      ended: 'ముగిసింది',
      minutes: '{n} నిమి',
      stageSearching: 'ఆటో కోసం వెతుకుతోంది',
      stageSearchingSub: 'దగ్గరలోని అన్నలను అడుగుతున్నాం…',
      stageArriving: 'అన్న చేరుకునే సమయం',
      stageArrivingNoEta: 'అన్న వస్తున్నారు',
      stageArrivingSub: 'పికప్‌కు వస్తున్నారు',
      arrivePickupAround: 'సుమారు {time}కి పికప్‌కు చేరుకుంటారు',
      stageArrived: 'అన్న చేరుకున్నారు',
      stageArrivedSub: 'పికప్ వద్ద వేచి ఉన్నారు',
      stageOnTrip: 'గమ్యం చేరుకునే సమయం',
      stageOnTripNoEta: 'ప్రయాణం జరుగుతోంది',
      stageOnTripSub: 'డ్రాప్‌కు వెళ్తున్నారు',
      arriveAround: 'సుమారు {time}కి చేరుకుంటారు',
      stageEnded: 'రైడ్ పూర్తయింది',
      stageCancelled: 'రైడ్ రద్దయింది',
      stageEndedSub: 'ట్రిప్ ముగియడంతో ఈ లింక్ షేరింగ్ ఆగిపోయింది.',
      completedAt: '{time}కి రైడ్ పూర్తయింది',
      cancelledAt: '{time}కి రైడ్ రద్దయింది',
      doneThanks: 'ట్రిప్‌ని ఫాలో చేసినందుకు ధన్యవాదాలు.',
      cancelledNote: 'ఈ రైడ్ జరగలేదు.',
      expiredTitle: 'ఈ లింక్ గడువు ముగిసింది',
      expiredSub: 'షేర్ చేసిన ట్రిప్ లింక్‌లు రైడ్ ముగిసిన 12 గంటల తర్వాత పనిచేయవు.',
      vehicleType: 'ఆటో రిక్షా',
      vehicleWithModel: 'ఆటో రిక్షా · {model}',
      verified: 'ధృవీకరించబడిన అన్న',
      ratingAria: '5కి {rating} రేటింగ్',
      safetyHelp: 'భద్రత & సహాయం',
      helpBody: 'ఈ రైడ్ గురించి ఆందోళనగా ఉందా? సహాయం కోసం కాల్ చేయండి. గోప్యత కోసం అన్న నంబర్ ఈ పేజీలో చూపించబడదు.',
      helpBodyNamed: '{name} రైడ్ గురించి ఆందోళనగా ఉందా? సహాయం కోసం కాల్ చేయండి. గోప్యత కోసం అన్న నంబర్ ఈ పేజీలో చూపించబడదు.',
      call112: '112కు కాల్ · అత్యవసరం',
      callSupport: 'MyTownly సపోర్ట్‌కు కాల్',
      needAuto: '{city}లో ఆటో కావాలా?',
      bookOwn: '{city}లో మీ ఆటో బుక్ చేసుకోండి',
      dropAt: 'డ్రాప్',
      tripDetails: 'ట్రిప్ వివరాలు',
      hideDetails: 'వివరాలు దాచు',
      tripTime: 'ప్రయాణ సమయం',
      arriveBy: 'చేరుకునే సమయం',
      pickupBy: 'పికప్ సమయం',
      callAnna: 'అన్న‌కు కాల్',
      annaPhone: '+91 {phone}',
      notFound: 'లింక్ అందుబాటులో లేదు',
      notFoundSub: 'ఈ ట్రిప్ లింక్ గడువు ముగిసింది లేదా సరైనది కాదు.',
      openApp: 'MyTownly Rides తెరవండి',
    },
    history: {
      today: 'ఈరోజు',
      yesterday: 'నిన్న',
      doneToRides: 'రైడ్స్‌కి తిరిగి',
      title: 'నా రైడ్స్',
      subtitle: 'మీ రైడ్ చరిత్ర & వివరాలు',
      empty: 'ఇంకా రైడ్స్ లేవు',
      emptySub: 'హోమ్ నుండి మొదటి ఆటో బుక్ చేయండి',
      filter_all: 'అన్నీ',
      filter_completed: 'పూర్తయినవి',
      filter_cancelled: 'రద్దయినవి',
      filter_active: 'యాక్టివ్',
      filter_past: 'గతం',
      totalRides: 'మొత్తం రైడ్స్',
      completedRides: 'పూర్తయినవి',
      distanceCovered: 'దూరం',
      co2Saved: 'CO2 ఆదా',
      rebook: 'మళ్ళీ బుక్',
      greenFootprintTitle: 'గ్రీన్ ఫుట్‌ప్రింట్',
      greenFootprintSub: 'MyTownly రైడ్స్‌తో మీరు {co2} కిలోల CO2 ను ఆదా చేశారు.',
      tapToOpen: 'తెరవడానికి ట్యాప్ చేయండి',
      loadOlder: 'పాత రైడ్స్ లోడ్ చేయండి',
      loadingOlder: 'పాత రైడ్స్ లోడ్ అవుతున్నాయి…',
      loadOlderFailed: 'పాత రైడ్స్ లోడ్ కాలేదు',
      noCompleted: 'పూర్తయిన రైడ్స్ లేవు',
      noCancelled: 'రద్దయిన రైడ్స్ లేవు',
      noMatchMore: 'మీ పాత రైడ్స్ ఇంకా లోడ్ కాలేదు.',
      showAll: 'అన్ని రైడ్స్ చూపించు',
    },
    profile: {
      emergencyProtectionNote: 'ఎమర్జెన్సీ రక్షణ: మీరు SOS నొక్కినప్పుడు, MyTownly బృందం మరియు మీ కాంటాక్ట్‌లకు తక్షణ లైవ్-లొకేషన్ అప్‌డేట్‌లు అందుతాయి.',
      contactName: 'పేరు',
      registeredPhone: 'ఫోన్ నంబర్',
      trustedContactsMax: 'నమ్మకమైన కాంటాక్ట్‌లు (గరిష్టం 3)',
      contactNum: 'కాంటాక్ట్ {n}',
      removeContact: 'తొలగించు',
      addContact: '+ మరో నమ్మకమైన కాంటాక్ట్ జోడించండి',
      title: 'ప్రొఫైల్',
      subtitle: 'మీ ఖాతా & ప్రాధాన్యతలు',
      phone: 'ఫోన్',
      logout: 'లాగ్ అవుట్',
      logoutSub: 'మీ ఖాతా నుండి లాగ్ అవుట్ చేయండి',
      logoutConfirmTitle: 'లాగ్ అవుట్ చేయాలా?',
      logoutConfirmBody: 'మళ్ళీ సైన్ ఇన్ అవ్వాలంటే మీ ఫోన్ నంబర్‌ను మళ్ళీ ధృవీకరించాలి.',
      logoutConfirmYes: 'లాగ్ అవుట్',
      help: 'WhatsApp సహాయం',
      callSupport: 'సపోర్ట్ కాల్',
      editName: 'పేరు మార్చు',
      editProfile: 'ప్రొఫైల్ మార్చు',
      saveName: 'సేవ్',
      cancelEdit: 'రద్దు',
      nameSaved: 'పేరు అప్‌డేట్ అయింది',
      ridesDone: 'పూర్తి రైడ్స్',
      ridesCompleted: '{n} రైడ్స్ పూర్తి',
      reviewsCount: '{n} రివ్యూలు',
      joined: '{month} {year} నుండి',
      verifiedPassenger: 'వెరిఫైడ్ ప్రయాణీకుడు',
      phoneVerified: 'ఫోన్ ధృవీకరించబడింది',
      language: 'భాష',
      languageHint: 'తెలుగు లేదా ఇంగ్లీష్ ఒక ట్యాప్‌లో మార్చండి',
      ratingHint: 'మీ ప్రయాణీకుల రేటింగ్',
      appVersion: 'యాప్ వెర్షన్ v{version}',
      terms: 'సేవా నిబంధనలు',
      termsSub: 'నిబంధనలు, బుకింగ్‌లు మరియు ఛార్జీల అంచనాలు',
      privacy: 'గోప్యతా విధానం',
      privacyPolicySub: 'మేము మీ డేటాను ఎలా సేకరిస్తాము మరియు రక్షిస్తాము',
      accountGroup: 'ఖాతా',
      savedPlacesSub: 'మీ సేవ్ చేసిన స్థలాలను నిర్వహించండి',
      safetyGroup: 'రక్షణ',
      safetyCenter: 'సేఫ్టీ సెంటర్',
      safetyCenterSub: 'రక్షణ సాధనాలు, SOS మరియు రైడ్ భద్రత',
      emergencyContactsSub: 'మీ అత్యవసర పరిచయాలను జోడించండి లేదా నిర్వహించండి',
      supportGroup: 'సహాయం',
      helpSupport: 'సహాయం & సపోర్ట్',
      helpSupportSub: 'రైడ్స్, చెల్లింపులు లేదా ఖాతాతో సహాయం పొందండి',
      privacyLegalGroup: 'గోప్యత & చట్టపరమైనవి',
      privacyCenter: 'గోప్యతా కేంద్రం',
      privacyCenterSub: 'మీ డేటా, సమ్మతి & ప్రాధాన్యతలను నిర్వహించండి',
      noNotifications: 'కొత్త నోటిఫికేషన్‌లు లేవు',
      actionsGroup: 'చర్యలు',
      deleteAccount: 'ఖాతాను తొలగించు',
      deleteAccountSub: 'మీ ఖాతా మరియు డేటాను శాశ్వతంగా తొలగించండి',
      rateApp: 'Play Storeలో MyTownlyకి రేటింగ్ ఇవ్వండి',
      rateAppSub: 'MyTownlyతో మీ అనుభవాన్ని పంచుకోండి',
    },
    app: {
      blockedTitle: 'ఖాతా బ్లాక్ అయింది',
      blockedBody: 'తప్పు అనిపిస్తే MyTownly సపోర్ట్‌ను సంప్రదించండి.',
      skipToContent: 'కంటెంట్‌కు వెళ్ళు',
      errorTitle: 'ఏదో పొరపాటు జరిగింది',
      errorBody: 'మీ రైడ్ స్క్రీన్ లోడ్ చేస్తున్నప్పుడు తాత్కాలిక సమస్య ఎదురైంది.',
      reloadScreen: 'స్క్రీన్ రీలోడ్ చేయి',
      returnHome: 'హోమ్‌కి తిరిగి వెళ్ళు',
    },
    cancel: {
      title: 'రైడ్ రద్దు చేయాలా?',
      subtitle: 'కారణం చెప్పండి',
      keep: 'ఉంచు',
      confirm: 'రద్దు చేయి',
      policyGraceTitle: 'ఉచిత రద్దు సమయం',
      policyGraceRemaining: '{time} మిగిలి ఉంది',
      policyCourtesyTitle: 'అన్న ఇప్పటికే బయలుదేరారు',
      policyCourtesyBody: 'ఈ రద్దు సౌజన్యంగా ఉచితం. తర్వాతిసారి నుండి ₹10 రుసుము వర్తిస్తుంది.',
      policyFeeTitle: '₹10 రద్దు రుసుము వర్తిస్తుంది',
      policyFeeBody: '₹10 ఆలస్య రద్దు రుసుము మీ తదుపరి రైడ్ ఛార్జీకి జోడించబడుతుంది.',
      reasons: {
        wait_too_long: 'అన్న రావడానికి ఎక్కువ సమయం పడుతోంది',
        wrong_pickup: 'పికప్ తప్పు',
        changed_plans: 'ప్లాన్ మారింది',
        found_other: 'వేరే రైడ్ బుక్ చేశాను',
        captain_issue: 'అన్నతో సమస్య',
        other: 'ఇంకేదైనా',
      },
      reasonsSearching: {
        wait_too_long: 'ఆటో దొరకడానికి ఎక్కువ సమయం పడుతోంది',
      },
      whyTitle: 'ఎందుకు రద్దు చేయాలనుకుంటున్నారు?',
      whyHint: 'ఒకటి ఎంచుకోండి. ఇంకా ఏదీ రద్దు కాలేదు.',
      keepWaiting: 'వేచి ఉంటాను',
      cancelRequest: 'అభ్యర్థన రద్దు చేయి',
      backToReasons: 'కారణాలకు తిరిగి',
      reasonChip: 'కారణం: {reason}',
      graceTitle: '{time} వరకు ఉచితంగా రద్దు చేయవచ్చు',
      graceBody: 'అన్న ఇప్పుడే అంగీకరించారు, ఎలాంటి ఛార్జీ లేదు.',
      noteLabel: 'ఏమి జరిగిందో చెప్పండి',
      noteOptional: '(ఐచ్ఛికం)',
      notePlaceholder: 'ఉదా: యాప్ తప్పు డ్రాప్ చూపించింది',
      noteHint: 'మీ తదుపరి రైడ్‌ను మెరుగుపరచడానికి సహాయపడుతుంది',
      fu: {
        waitSearchingTitle: 'మీ ఆటో కోసం ఇంకా వెతుకుతున్నాం',
        waitSearchingBody: 'దగ్గరలోని అన్నలు ఇప్పుడు బిజీగా ఉన్నారు. చాలా రైడ్‌లు ఒక నిమిషంలో అంగీకరించబడతాయి.',
        boostTitle: '₹{amount} జోడించి త్వరగా పొందండి',
        boostSub: 'కొత్త ఛార్జీ ₹{fare} · ఎక్కువ మంది అన్నలకు కనిపిస్తుంది',
        waitAssignedTitle: '{name} వస్తున్నారు',
        waitAssignedBody: 'ఆయన అంగీకరించి మీ పికప్‌కు వస్తున్నారు.',
        waitArrivedTitle: '{name} పికప్ వద్ద ఉన్నారు',
        waitArrivedBody: 'ఆయన ఇప్పుడు మీ కోసం వేచి ఉన్నారు.',
        callTitle: '{name}కు కాల్ చేయండి',
        callSub: 'ఎక్కడ ఉన్నారో అడగండి',
        pickupSearchingTitle: 'పికప్ తప్పు చోట ఉందా?',
        pickupSearchingBody: 'ఇంకా ఎవరూ అంగీకరించలేదు, కాబట్టి ఉచితంగా రద్దు చేసి సరైన చోటు నుండి మళ్లీ బుక్ చేయవచ్చు.',
        rebookTitle: 'రద్దు చేసి కొత్త పికప్ ఎంచుకోండి',
        rebookSub: 'మీ డ్రాప్ అలాగే ఉంటుంది',
        pickupRideTitle: 'పికప్ తప్పు చోట ఉందా?',
        pickupRideBody: 'మీరు ఎక్కడ ఉన్నారో {name}కు చెప్పండి. ఒక్క మెసేజ్‌తో చాలా పికప్ సమస్యలు తీరిపోతాయి.',
        chatTitle: '{name}కు మెసేజ్ చేయండి',
        chatSub: 'మీ ఖచ్చితమైన చోటు పంపండి',
        issueTitle: 'అన్నతో సమస్యా?',
        issueBody: 'మీకు అసురక్షితంగా అనిపిస్తే 112కు కాల్ చేయండి. ఏమి జరిగిందో చెప్పండి, మేము పరిశీలిస్తాం.',
        supportTitle: 'MyTownly సపోర్ట్‌కు కాల్',
        supportSub: 'మా టీమ్ ఫాలో అప్ చేస్తుంది',
        foundOtherBody: 'చెప్పినందుకు ధన్యవాదాలు. తదుపరిసారి త్వరగా ఆటో అందించడానికి ఇది సహాయపడుతుంది.',
        plainTitleSearching: 'ఈ అభ్యర్థన రద్దు చేయాలా?',
        plainTitleRide: 'ఈ రైడ్ రద్దు చేయాలా?',
        plainBodySearching: 'ఇంకా ఎవరూ అంగీకరించలేదు, కాబట్టి రద్దు చేయడానికి ఎలాంటి ఛార్జీ లేదు.',
        plainBodyAssigned: '{name} ఇప్పటికే మీ వద్దకు వస్తున్నారు.',
        plainBodyArrived: '{name} పికప్ వద్ద మీ కోసం వేచి ఉన్నారు.',
      },
    },
    rating: {
      title: 'అన్న‌ను రేట్ చేయండి',
      subtitle: 'రైడ్ ఎలా ఉంది?',
      commentPlaceholder: 'కామెంట్ (ఐచ్ఛికం)',
      fareWas: 'ఛార్జ్ ₹{fare}',
      submit: 'సబ్మిట్',
      skip: 'ఇప్పుడు వద్దు',
      thanks: 'ఫీడ్‌బ్యాక్‌కు ధన్యవాదాలు!',
      thankYou: 'ఫీడ్‌బ్యాక్‌కు ధన్యవాదాలు!',
      rateTrip: 'రేట్ చేయండి',
      quickFeedback: 'క్విక్ ఫీడ్‌బ్యాక్',
      tags: {
        polite: 'మర్యాదగా ఉన్నారు',
        clean: 'ఆటో శుభ్రంగా ఉంది',
        onTime: 'సమయానికి వచ్చారు',
        safeDriving: 'సేఫ్ డ్రైవింగ్',
        goodRoute: 'మంచి రూట్',
      },
    },

    chat: {
      title: 'చాట్',
      open: 'చాట్',
      empty: 'అన్న‌కు హాయ్ చెప్పండి',
      placeholder: 'మెసేజ్ టైప్ చేయండి…',
      send: 'పంపు',
      sendFailed: 'మెసేజ్ పంపలేకపోయాం',
    },
    pay: {
      title: 'పేమెంట్',
      cash: 'నగదు',
      cashHint: 'రైడ్ పూర్తయిన తర్వాత అన్న‌కు నగదు చెల్లించండి',
    },
    places: {
      savedLocations: 'సేవ్ చేసిన స్థలాలు',
      removeHome: 'హోమ్ తొలగించు',
      removeWork: 'వర్క్ తొలగించు',
      shortcutTip: 'సేవ్ చేసిన స్థలాలు బుకింగ్ మ్యాప్‌లో ఒక్క ట్యాప్ షార్ట్‌కట్‌లుగా కనిపిస్తాయి — పికప్ & డ్రాప్ వేగంగా.',
      title: 'సేవ్ చేసిన స్థలాలు',
      home: 'ఇల్లు',
      work: 'ఆఫీసు',
      notSet: 'సెట్ చేయలేదు',
      setPickup: 'చివరి పికప్',
      setDrop: 'చివరి డ్రాప్',
      setOnMap: 'మ్యాప్‌లో సెట్ చేయి',
      setHomeMap: 'ఇల్లు మ్యాప్‌లో సెట్ చేయి',
      setWorkMap: 'ఆఫీసు మ్యాప్‌లో సెట్ చేయి',
      confirmPin: 'ఈ స్థలం సేవ్ చేయి',
      mapHint: 'మ్యాప్‌లో పిన్ పెట్టండి — గత రైడ్ అవసరం లేదు',
      update: 'అప్‌డేట్',
      saved: 'సేవ్ అయింది',
      needRide: 'ముందు ఒక రైడ్ పూర్తి చేయండి',
      needLocation: 'మ్యాప్‌లో స్థలం ఎంచుకోండి',
    },
    push: {
      title: 'రైడ్ అలర్ట్స్ ఆన్ చేయండి',
      body: 'అన్న అంగీకరించినప్పుడు / వచ్చినప్పుడు ఫోన్ లాక్ ఉన్నా హెచ్చరిక వస్తుంది.',
      enable: 'నోటిఫికేషన్స్ ఆన్ చేయి',
      later: 'తర్వాత',
      enabled: 'నోటిఫికేషన్స్ ఆన్',
      denied: 'నోటిఫికేషన్స్ బ్లాక్ — సెట్టింగ్స్ → Apps → MyTownly Rides లో ఆన్ చేయండి',
      sheetTitle: 'మీ రైడ్ అప్‌డేట్‌లు పొందండి',
      sheetRideTitle: 'అన్న అంగీకరించారు',
      sheetRideBody: 'ఆటో కన్ఫర్మ్ అయినప్పుడు ఫోన్ లాక్ ఉన్నా హెచ్చరిక.',
      sheetArriveTitle: 'అన్న వస్తున్నారు',
      sheetArriveBody: 'పికప్ దగ్గరికి వచ్చినప్పుడు తెలుస్తుంది — సమయానికి బయటకు రావచ్చు.',
      sheetAllow: 'నోటిఫికేషన్స్ అనుమతించు',
      sheetLater: 'ఇప్పుడు వద్దు',
    },
    nav: { home: 'బుక్', rides: 'రైడ్స్', profile: 'ప్రొఫైల్', main: 'మెనూ', intertown: 'ఇంటర్ టౌన్' },
    riderNote: {
      add: 'అన్నకి ఒక సూచన రాయండి',
      addHint: 'గేటు, గుర్తు, లగేజీ… (ఐచ్ఛికం)',
      addShort: 'రాయి',
      edit: 'మార్చు',
      title: 'అన్నకి సూచన',
      sub: 'మీ అన్న మిమ్మల్ని సులభంగా కనుగొనేలా సహాయం చేయండి. సరిపోయేవి ఎంచుకోండి లేదా మీరే రాయండి.',
      placeholder: 'ఉదా: గుడి ఎదురుగా నీలం గేటు',
      privacy: 'రైడ్ అంగీకరించిన అన్న మాత్రమే, అంగీకరించాకే దీన్ని చూస్తారు.',
      save: 'సూచన సేవ్ చేయి',
      remove: 'సూచన తీసేయి',
      noPhone: 'దయచేసి ఫోన్ నంబర్ రాయకండి. అన్న యాప్ నుంచే మీకు కాల్ చేయగలరు.',
      yourNote: 'అన్నకి మీ సూచన',
      seen: 'అన్న మీ సూచన చూశారు',
      notSeenYet: 'అన్న ఇంకా తెరవలేదు',
      chip_gate: 'మెయిన్ గేట్ దగ్గర ఉంటాను',
      chip_call: 'వచ్చాక కాల్ చేయండి',
      chip_lane: 'సందు లోపలికి రండి',
      chip_luggage: 'లగేజీ ఉంది',
      chip_elder: 'పెద్దవారు ఉన్నారు, నెమ్మదిగా',
      chip_kids: 'పిల్లలతో వస్తున్నాం',
    },
    intertown: {
      kicker: 'MYTOWNLY INTERTOWN',
      offlineTitle: 'సమీప పట్టణాలు, ఇప్పుడు సులభం.',
      offlineBody: 'మీ ప్రాంతం కోసం ఇంటర్ టౌన్ బుకింగ్‌లు సిద్ధమవుతున్నాయి. దయచేసి త్వరలో మళ్ళీ చూడండి.',
      heroTitle: 'మీ పట్టణం దాటి ప్రయాణించండి.',
      heroBody: 'సమీప పట్టణాలకు నమ్మకమైన ఆటోను బుక్ చేసుకోండి.',
      fromRate: '{rate}/కి.మీ నుండి',
      fixedFare: 'నిర్దిష్ట ఛార్జీ',
      safeReturn: 'సురక్షిత తిరుగు ప్రయాణ ఎంపికలు',
      yourTrip: 'మీ ఇంటర్ టౌన్ ట్రిప్',
      cancel: 'రద్దు చేయండి',
      tripTypeLabel: 'ప్రయాణ రకం',
      oneWay: 'వన్ వే',
      oneWaySub: 'అక్కడ దించండి',
      returnTrip: 'రిటర్న్ ట్రిప్',
      returnSub: 'అన్న వేచి ఉండి మిమ్మల్ని వెనక్కి తీసుకొస్తారు',
      popularRoutes: 'ప్రసిద్ధ మార్గాలు',
      pickupLabel: 'మీ పికప్ పాయింట్',
      pickupPlaceholder: 'ఉదాహరణ: పైలేరు రైల్వే స్టేషన్, ప్రధాన ద్వారం',
      pickupHint: 'మీరు నిర్ధారించిన తర్వాత మాత్రమే దీన్ని కేటాయించిన అన్నతో పంచుకుంటాము.',
      pickupMapHint: 'ప్రస్తుత లొకేషన్, సెర్చ్ లేదా మ్యాప్ పిన్ ఉపయోగించండి.',
      choosePickup: 'మ్యాప్‌లో పికప్ ఎంచుకోండి',
      customDestination: 'మరో పట్టణం ఎంచుకోండి',
      customDestinationHint: 'మ్యాప్‌లో వెతకండి లేదా గమ్యస్థానాన్ని ఎంచుకోండి',
      customLimits: 'వన్ వే {min}–{max} కి.మీ',
      destinationOutsideRoute: 'మీ పికప్ నుండి {max} కి.మీ లోపు గమ్యస్థానాన్ని ఎంచుకోండి',
      exactSpotCta: '{town}లో ఖచ్చితమైన చోటు ఎంచుకోండి',
      exactSpotChange: 'చోటు మార్చండి',
      useTownCentre: 'పట్టణ కేంద్రం ఉపయోగించండి',
      exactSpotNote: '{town}లో మీరు ఎంచుకున్న చోటు · ప్రసిద్ధ మార్గం ఛార్జీ',
      townSpotTitle: '{town}లో ఒక చోటు ఎంచుకోండి',
      townSpotSubtitle: '{town} నుండి {max} కి.మీ లోపు వెతకండి లేదా మ్యాప్‌లో ఎంచుకోండి. ప్రసిద్ధ మార్గం ఛార్జీ మారదు.',
      townSpotOutside: '{town} నుండి {max} కి.మీ లోపు చోటు ఎంచుకోండి',
      switchedToPopular: '{town} ప్రసిద్ధ మార్గం, కాబట్టి దాని నిర్దిష్ట ఛార్జీ వర్తిస్తుంది. మీరు ఎంచుకున్న చోటు అలాగే ఉంటుంది.',
      switchedToDistance: 'ఈ ట్రిప్ రకానికి {town} కోసం నిర్దిష్ట ఛార్జీ లేదు, కాబట్టి దూరం ఆధారంగా లెక్కిస్తాము.',
      pickupOutsideArea: 'పికప్ పైలేరు నుండి {max} కి.మీ లోపు ఉండాలి',
      etaPickup: 'పికప్‌కు ETA',
      etaDestination: 'గమ్యస్థానానికి ETA',
      elapsed: 'ప్రయాణ సమయం',
      change: 'మార్చండి',
      choose: 'ఎంచుకోండి',
      pickupNotePlaceholder: 'అన్న కోసం ల్యాండ్‌మార్క్ లేదా గేట్ వివరాలు (ఐచ్ఛికం)',
      pickupMapTitle: 'పికప్ పాయింట్ ఎంచుకోండి',
      pickerSetPickup: 'పికప్ ఎంచుకోండి',
      pickerSetDestination: 'గమ్యస్థానం ఎంచుకోండి',
      pickerMovePickup: 'పికప్ సర్దుబాటు చేయడానికి మ్యాప్ కదపండి',
      pickerMoveDestination: 'గమ్యస్థానం సర్దుబాటు చేయడానికి మ్యాప్ కదపండి',
      pickerMoving: 'మీ ప్రదేశంపై పిన్ ఉంచండి',
      pickerMovingDestination: 'మీ గమ్యస్థానంపై పిన్ ఉంచండి',
      pickerSearchOrMove: 'ప్రదేశం ఎంచుకోవడానికి వెతకండి లేదా మ్యాప్ కదపండి',
      pickerLocating: 'మీ ప్రస్తుత ప్రదేశం తెలుసుకుంటోంది…',
      pickerCheckPin: 'మీ పికప్ పాయింట్ తనిఖీ చేసి, అవసరమైతే పిన్ సర్దుబాటు చేయండి.',
      pickerAddressFailed: 'ఈ చిరునామా దొరకలేదు. మళ్ళీ ప్రయత్నించండి లేదా ప్రదేశం వెతకండి.',
      pickerFareAfter: 'మీరు నిర్ధారించిన తర్వాత ఛార్జీ లెక్కిస్తాము',
      pickerConfirmPickup: 'పికప్ నిర్ధారించండి',
      pickerConfirmDestination: 'గమ్యస్థానం నిర్ధారించండి',
      destinationMapTitle: 'మీ గమ్యస్థానం ఎంచుకోండి',
      pickupMapSubtitle: 'ప్రస్తుత లొకేషన్, సెర్చ్ లేదా మ్యాప్ పిన్ ఉపయోగించండి.',
      destinationMapSubtitle: 'సమీప పట్టణాన్ని వెతకండి లేదా మ్యాప్‌లో ఖచ్చితమైన గమ్యస్థానాన్ని ఎంచుకోండి.',
      searchLocation: 'ప్రదేశం వెతకండి',
      locationSearchUnavailable: 'మ్యాప్ సెర్చ్ ప్రస్తుతం అందుబాటులో లేదు. కొద్దిసేపటి తర్వాత ప్రయత్నించండి.',
      locationNotFound: 'ఈ ప్రదేశం కనిపించలేదు. మరో సెర్చ్ చేయండి లేదా మ్యాప్‌లో ట్యాప్ చేయండి.',
      currentLocationFailed: 'ప్రస్తుత లొకేషన్ పొందలేకపోయాం. మ్యాప్‌లో ఎంచుకోండి.',
      closeLocationPicker: 'లొకేషన్ ఎంపిక మూసివేయండి',
      tapMapHint: 'పైన వెతకండి లేదా మ్యాప్‌పై ట్యాప్ చేసి పిన్ పెట్టండి',
      useMyLocation: 'నా లొకేషన్ ఉపయోగించండి',
      selectedPickup: 'ఎంచుకున్న పికప్',
      selectedDestination: 'ఎంచుకున్న గమ్యస్థానం',
      tapOrSearch: 'మ్యాప్‌లో ట్యాప్ చేయండి లేదా పైన వెతకండి',
      resolvingLocation: 'చిరునామా తెలుసుకుంటోంది…',
      useThisLocation: 'ఈ లొకేషన్ ఉపయోగించండి',
      pleaseChoosePickup: 'మ్యాప్‌లో మీ పికప్ పాయింట్ ఎంచుకోండి',
      pleaseChooseDestination: 'మ్యాప్‌లో మీ గమ్యస్థానం ఎంచుకోండి',
      waitingTitle: 'వేచి ఉండే సమయం మీ చేతిలోనే',
      waitingBody: '{free} నిమిషాలు ఉచితం, తర్వాత {rate} / {unit}. గరిష్టంగా {max} నిమిషాలు.',
      unitMinute: 'నిమిషం',
      unitHour: 'గంట',
      calculatingFare: 'సురక్షిత ఛార్జీని లెక్కిస్తోంది…',
      calculatingShort: 'లెక్కిస్తోంది…',
      sendingRequest: 'అభ్యర్థన పంపుతోంది…',
      finishCurrentRide: 'ముందు మీ ప్రస్తుత రైడ్ పూర్తి చేయండి',
      requestAnna: 'ఆటోను అభ్యర్థించండి · {fare}',
      requestAnnaCta: 'ఆటోను అభ్యర్థించండి',
      pageTitle: 'ఇంటర్ టౌన్',
      chipLiveTracking: 'లైవ్ ట్రిప్ ట్రాకింగ్',
      chipVerifiedAnna: 'ధృవీకరించిన అన్న',
      perKm: '{rate}/కి.మీ',
      fromFare: '{fare} నుండి',
      pickupRadiusNote: 'పైలేరు నుండి {max} కి.మీ లోపు పికప్‌లు అందుబాటులో ఉన్నాయి',
      pickupShort: 'పికప్',
      destinationShort: 'గమ్యస్థానం',
      editPickup: 'పికప్ మార్చండి',
      setOnMap: 'మ్యాప్‌లో ఎంచుకోండి',
      fixedFareRoute: 'ఈ రూట్‌కు నిర్దిష్ట ఛార్జీ',
      fareForRoute: 'ఈ రూట్‌కు ఛార్జీ',
      yourFare: 'మీ ఛార్జీ',
      fareFromDistance: 'దూరం ఆధారంగా ఛార్జీ లెక్కిస్తాము',
      chooseDestinationFare: 'ఛార్జీ చూడటానికి గమ్యస్థానం ఎంచుకోండి',
      fareByDistance: 'దూరం ఆధారంగా ఛార్జీ',
      returnShort: 'రిటర్న్',
      selected: 'ఎంచుకున్నది',
      somewhereElse: 'ఇంకెక్కడికైనా వెళ్లాలా?',
      somewhereElseBody: 'మ్యాప్‌లో వెతకండి లేదా పిన్ ఎంచుకోండి',
      privacyNote: 'అన్న అంగీకరించిన తర్వాత మాత్రమే మీ పికప్ వివరాలు పంచుకుంటాము.',
      choosePickupCta: 'పికప్ ఎంచుకోండి',
      chooseDestinationCta: 'గమ్యస్థానం ఎంచుకోండి',
      changePickupCta: 'పికప్ మార్చండి',
      changeDestinationCta: 'గమ్యస్థానం మార్చండి',
      retry: 'మళ్లీ ప్రయత్నించండి',
      pleaseAddPickup: 'దయచేసి మీ పికప్ ప్రదేశాన్ని తెలియజేయండి',
      quoteFailed: 'ఇంటర్ టౌన్ ఛార్జీని లోడ్ చేయలేకపోయాము',
      routeDestinationFailed: 'ఈ రూట్ గమ్యస్థానాన్ని సిద్ధం చేయలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.',
      routeDestinationSlow: 'ఊరును కనుగొనడానికి ఎక్కువ సమయం పడుతోంది. కనెక్షన్ చెక్ చేసి మళ్లీ ప్రయత్నించండి.',
      quoteSlow: 'ఛార్జీని చూడటానికి ఎక్కువ సమయం పడుతోంది. కనెక్షన్ చెక్ చేసి మళ్లీ ప్రయత్నించండి.',
      requestSent: 'ఇంటర్ టౌన్ అభ్యర్థన పంపబడింది. మేము త్వరలో ఒక అన్నను నిర్ధారిస్తాము.',
      requestFailed: 'మీ అభ్యర్థనను పంపలేకపోయాము',
      confirmCancel: 'ఈ ఇంటర్ టౌన్ అభ్యర్థనను రద్దు చేయాలా?',
      cancelledToast: 'ఇంటర్ టౌన్ అభ్యర్థన రద్దు చేయబడింది',
      cancelFailed: 'అభ్యర్థనను రద్దు చేయలేకపోయాము',
      errTooClose: 'పికప్, గమ్యం చాలా దగ్గరగా ఉన్నాయి. ఇంకొంచెం దూరంలో గమ్యం ఎంచుకోండి.',
      errPickupOutside: 'మీ పికప్ ఇంటర్ టౌన్ పరిధికి బయట ఉంది. పట్టణానికి దగ్గరగా పికప్ ఎంచుకోండి.',
      errDestinationRange: 'ఆ గమ్యం ఈ ట్రిప్ అనుమతించిన దూరానికి బయట ఉంది. వేరొకటి ఎంచుకోండి.',
      errAlreadyActive: 'మీకు ఇప్పటికే యాక్టివ్ ఇంటర్ టౌన్ అభ్యర్థన ఉంది.',
      errUnavailable: 'ఇంటర్ టౌన్ బుకింగ్స్ ప్రస్తుతం అందుబాటులో లేవు. తర్వాత ప్రయత్నించండి.',
      liveMapLabel: 'లైవ్ ట్రిప్ మ్యాప్',
      trip: {
        chipFinding: 'వెతుకుతోంది',
        chipOnTheWay: 'వస్తున్నారు',
        chipAtPickup: 'పికప్ వద్ద',
        chipInProgress: 'ప్రయాణంలో',
        chipWaiting: 'వేచి ఉన్నారు',
        chipReturning: 'తిరిగి వస్తున్నారు',
        stepFinding: 'వెతుకుతోంది',
        stepOnTheWay: 'వస్తున్నారు',
        stepAtPickup: 'పికప్ వద్ద',
        findingTitle: 'అందుబాటులో ఉన్న అన్నను వెతుకుతున్నాము…',
        findingBody: 'అన్నలు మీ ట్రిప్‌ను చూసి అంగీకరించవచ్చు. అన్న వచ్చే వరకు మీరు రద్దు చేయవచ్చు.',
        findingFor: '{time} నుండి వెతుకుతున్నాము',
        onTheWayTitle: 'అన్న వస్తున్నారు',
        onTheWayEta: 'సుమారు {min} నిమిషాల్లో',
        onTheWayBody: 'మీ పికప్ వద్ద సిద్ధంగా ఉండండి.',
        arrivedTitle: 'మీ అన్న వచ్చారు',
        arrivedBody: 'ఎక్కిన తర్వాత మాత్రమే ట్రిప్ PIN చెప్పండి.',
        pinLabel: 'ట్రిప్ PIN',
        etaHrMin: 'సుమారు {h} గంట {m} నిమిషాలు',
        etaHr: 'సుమారు {h} గంట',
        etaMin: 'సుమారు {m} నిమిషాలు',
        etaNow: 'ఇప్పుడే చేరుకుంటున్నారు',
        arriveAround: 'సుమారు {time}కి చేరుకుంటారు',
        tripTime: 'ప్రయాణ సమయం {time}',
        etaUpdating: 'చేరే సమయం అప్‌డేట్ అవుతోంది',
        headingTo: '{place}కు వెళ్తున్నారు',
        delayedTitle: 'లొకేషన్ అప్‌డేట్ ఆలస్యమైంది',
        delayedBody: 'ప్రస్తుతం చేరే సమయం తెలియదు',
        lastUpdate: 'చివరి అప్‌డేట్ {min} నిమిషాల క్రితం',
        liveNow: 'లొకేషన్ ఇప్పుడే అప్‌డేట్ అయింది',
        liveStale: 'లొకేషన్ అప్‌డేట్ ఆలస్యమైంది',
        annaDetails: 'అన్న వివరాలు',
        call: 'కాల్',
        shareDetails: 'వివరాలు షేర్ చేయండి',
        shareHint: 'డ్రైవర్, వాహనం నంబర్, రూట్, లైవ్ ట్రాకింగ్ లింక్‌ను WhatsApp ద్వారా షేర్ చేయండి',
        safetyHelp: 'భద్రత & సహాయం',
        safetyTitle: 'భద్రత & సహాయం',
        call112: '112కు కాల్ చేయండి',
        call112Sub: 'అత్యవసర పరిస్థితుల కోసం. MyTownlyకు కూడా అలర్ట్ వెళ్తుంది',
        alertTeam: 'MyTownly & కుటుంబానికి అలర్ట్',
        alertTeamSub: 'MyTownlyకు అలర్ట్ పంపి, కుటుంబానికి పంపే మెసేజ్‌ను తెరుస్తుంది',
        alertTeamDone: 'MyTownlyకు అలర్ట్ అయింది',
        alertTeamDoneSub: 'మెసేజ్ తెరిస్తే, కుటుంబానికి తెలియజేయడానికి Send నొక్కండి',
        alertShareReady: 'MyTownlyకు అలర్ట్ అయింది. కుటుంబానికి తెలియజేయడానికి Send నొక్కండి.',
        callSupport: 'MyTownly సపోర్ట్‌కు కాల్ చేయండి',
        callSupportSub: 'మీ ట్రిప్‌లో సహాయం పొందండి',
        reportIssue: 'సమస్యను తెలియజేయండి',
        reportIssueSub: 'ఏమి జరిగిందో మాకు చెప్పండి',
        tripFare: 'ట్రిప్ ఛార్జీ',
        baseFare: 'బేస్ ఛార్జీ',
        waitingCharge: 'వేచి ఉండే ఛార్జీ',
        soFar: 'ఇప్పటివరకు {amount}',
        waitingAt: '{place} వద్ద వేచి ఉన్నారు',
        waitedFor: '{n} నిమిషాలుగా వేచి ఉన్నారు',
        waitFree: '{n} నిమిషాలు ఉచితం',
        waitThenMinute: 'తర్వాత నిమిషానికి {rate}',
        waitThenHour: 'తర్వాత గంటకు {rate}',
        returnNote: 'అన్న తిరుగు ప్రయాణం మొదలుపెట్టినప్పుడు తిరుగు ప్రయాణం ప్రారంభమవుతుంది',
        returningTitle: '{place}కు తిరిగి వెళ్తున్నారు',
        returningNote: 'తిరుగు ప్రయాణంలో కూడా లైవ్ లొకేషన్ కొనసాగుతుంది',
        cancelTrip: 'ట్రిప్ రద్దు చేయండి',
        vehicleAuto: 'ఆటో',
      },
      mapUpdatedNow: 'అన్న లొకేషన్ లైవ్‌లో ఉంది',
      mapWaitingLocation: 'అన్న లొకేషన్‌కు కనెక్ట్ అవుతోంది…',
      mapLocationStale: 'అన్న లొకేషన్ కొంతసేపటి నుండి అప్‌డేట్ కాలేదు',
      atPickup: 'అన్న మీ పికప్ వద్ద ఉన్నారు',
      rebookUnavailable: 'ఆ ట్రిప్‌ను ఇప్పుడు మళ్లీ బుక్ చేయలేము. దయచేసి మీ ట్రిప్‌ను ఎంచుకోండి.',
      timeline: {
        title: 'ట్రిప్ టైమ్‌లైన్',
        requested: 'ట్రిప్ అభ్యర్థించారు',
        accepted: 'అన్న మీ ట్రిప్‌ను అంగీకరించారు',
        arrivedPickup: 'అన్న మీ పికప్‌కు వచ్చారు',
        started: 'ట్రిప్ మొదలైంది',
        reachedDestination: 'గమ్యస్థానానికి చేరుకున్నారు',
        reachedAndCompleted: 'గమ్యస్థానానికి చేరుకున్నారు · ట్రిప్ పూర్తయింది',
        returnStarted: 'తిరుగు ప్రయాణం మొదలైంది',
        waited: 'గమ్యస్థానం వద్ద {min} నిమిషాలు వేచి ఉన్నారు',
        completed: 'ట్రిప్ పూర్తయింది',
        cancelledByYou: 'మీరు అభ్యర్థనను రద్దు చేశారు',
        cancelledByAdmin: 'MyTownly రద్దు చేసింది',
        cancelledNoAnna: 'సమయానికి ఏ అన్నను నిర్ధారించలేకపోయాము',
        cancelled: 'అభ్యర్థన రద్దయింది',
      },
      recenterMap: 'మ్యాప్‌ను మధ్యలోకి తీసుకురండి',
      status: {
        pending_admin_confirmation: 'మేము ఒక అన్నను నిర్ధారిస్తున్నాము',
        open_for_claim: 'అందుబాటులో ఉన్న అన్నను వెతుకుతున్నాము',
        awaiting_driver_acceptance: 'అన్న మీ ట్రిప్‌ను పరిశీలిస్తున్నారు',
        confirmed: 'అన్న నిర్ధారించారు — సిద్ధంగా ఉండండి',
        arrived_at_pickup: 'అన్న పికప్‌కు వచ్చారు — మీ ట్రిప్ PIN చెప్పండి',
        started: 'ట్రిప్ మొదలైంది — వెళ్తున్నారు',
        at_destination: 'అన్న గమ్యస్థానం వద్ద వేచి ఉన్నారు',
        returning: 'మీ తిరుగు ప్రయాణం జరుగుతోంది',
        completed: 'ట్రిప్ పూర్తయింది',
        cancelled: 'రద్దు చేయబడింది',
        updating: 'అప్‌డేట్ అవుతోంది',
      },
    },
    status: {
      searching: 'వెతుకుతోంది',
      driver_assigned: 'అన్న కేటాయించబడింది',
      arrived: 'వచ్చారు',
      started: 'ప్రయాణంలో',
      completed: 'పూర్తి',
      cancelled: 'రద్దు',
    },
    tour: {
      next: 'తర్వాత',
      skip: 'దాటవేయి',
      done: 'సరే',
      pickup: {
        title: 'పికప్ & డ్రాప్ సెట్ చేయండి',
        body: 'మీ పికప్ నిర్ధారించి, ఎక్కడికి వెళ్లాలో ఎంచుకోండి.',
      },
      where: {
        title: 'ఎక్కడికి?',
        body: 'డ్రాప్ సెర్చ్ చేయండి లేదా మ్యాప్‌లో పిన్ చేయండి. ఊరి బయట ఉంటే ముందు {city} పికప్ సెట్ చేయండి.',
      },
      shortcuts: {
        title: 'హోమ్ & వర్క్',
        body: 'ప్రొఫైల్‌లో సేవ్ చేసిన ప్రదేశాలకు ఒక్క ట్యాప్ — రోజువారీ రూట్లు వేగంగా.',
      },
      book: {
        title: 'ఛార్జ్ చూసి బుక్ చేయండి',
        body: 'అంచనా ఛార్జ్ చూసి Book Auto నొక్కండి.',
      },
      otp: {
        title: 'అన్న‌కు OTP చెప్పండి',
        body: 'అన్న వచ్చిన తర్వాత 4 అంకెల OTP చెప్పి ప్రయాణం మొదలుపెట్టండి.',
      },
      track: {
        title: 'లైవ్ ట్రాక్ & సేఫ్టీ',
        body: 'మ్యాప్‌లో ట్రిప్ చూడండి, రైడ్ షేర్ చేయండి, అవసరమైతే SOS నొక్కండి.',
      },
    },
    requiredUpdate: {
      versionLine: 'వెర్షన్ {version} లేదా అంతకంటే కొత్తది అవసరం',
      updateNow: 'ఇప్పుడు అప్‌డేట్ చేయండి',
      opening: 'Google Play తెరుస్తోంది…',
      idleNote: 'మీరు యాక్టివ్ రైడ్ లేదా బుకింగ్‌లో లేనప్పుడు మాత్రమే ఇది కనిపిస్తుంది.',
    },
  },
};
